import { Rectangle, Container, Graphics, Texture, Text, TextStyle, Sprite, Assets, Filter, ColorMatrixFilter, SCALE_MODES, AnimatedSprite, Spritesheet } from 'pixi.js';
import { OutlineFilter } from 'pixi-filters/outline';
// import { Stats } from 'pixi-stats'; // Disabled due to initialization issues
import { defineQuery } from 'bitecs';

import { Position, Movement, Player, BIOME_COLORS, Plant, PLANT_TYPES, Animal, ANIMAL_TYPES, AI, AI_STATE, Corpse, Velocity, Rock, ROCK_TYPES, Structure, Production } from '../world.js';
import { TILE_SIZE, TILE_SCALE, PLAYER_SIZE, RENDER_RADIUS, SUB_TILE_SIZE, ENTITY_BASE_RADIUS, MAP_WIDTH, MAP_HEIGHT, CHUNK_SIZE, CHUNKS_IN_VIEW, DAY_LIGHTING_RADIUS } from '../config.js';
import { tileManager, TILE_TYPES, BIOME_TYPES } from './tileManager.js';
import { MIN_REPRODUCTION_ENERGY } from './plants.js';
import { waterVertexShader, waterFragmentShader, waterShaderUniforms } from '../shaders/waterShader.js';
import { setApp } from '../entities/bee.js'; // Keep for bee initialization if needed
import { createBee } from '../entities/bee.js';
import { createHive } from '../entities/hive.js';
import { getBeeData, BEE_CARRYING_CAPACITY } from './beeSystem.js';

// --- Constants from Original --- 
const PLANT_SIZE_MULTIPLIERS = {
    2: 0.5, // Mushroom
    3: 0.5  // Herb
};

// Animal size multipliers (relative to tile size)
const ANIMAL_SIZES = {
    CHICKEN: 0.4,  // 40% of tile size
    RABBIT: 0.35,  // 35% of tile size
    GOAT: 0.5,     // 50% of tile size
    HAWK: 0.9      // 90% of tile size (using value from entityData)
};

// Map biome index to asset key (must match BIOME_TYPES order)
const BIOME_ASSET_KEYS = {
    0: 'water',    // If you have a water image, otherwise fallback to color
    1: 'beach',
    2: 'plains',
    3: 'forest',
    4: 'mountains'
};

const TILE_TEXTURE_SIZE = 16; // Assuming tile textures are 16x16
const tileTextureCache = new Map(); // Cache for generated tile textures

// --- Global State (Managed primarily within RenderSystem) ---
let app = null;
const textureCache = new Map();
const playerFrames = new Map();
let playerSpritesheet = null;
let playerSpritesheetReady = false;

// --- Utility Functions ---

// Integrated Texture Caching (Replaces need for separate textureCache.js)
function getCachedTexture(assetName, resources) {
    if (textureCache.has(assetName)) {
        const cachedTexture = textureCache.get(assetName);
        if (cachedTexture && cachedTexture.width > 0 && cachedTexture.height > 0) {
            return cachedTexture;
        }
        textureCache.delete(assetName);
    }
    
    try {
        const pathVariations = [
            assetName,
            `game-assets-${assetName}`,
            `assets/images/${assetName}.png`,
            `assets/images/structures/${assetName}.png`,
            `${assetName}.png`
        ];
        
        console.log(`[TextureCache] Attempting to load texture for '${assetName}'`);
        console.log(`[TextureCache] Trying path variations:`, pathVariations);
        
        let texture = null;
        
        // 1. Try the provided 'resources' object (e.g., from PIXI loader)
        if (resources) {
            for (const path of pathVariations) {
                texture = resources[path];
                if (texture && texture.width > 0 && texture.height > 0) {
                    console.log(`[TextureCache] Found texture in resources: ${path}`);
                    break;
                }
            }
        }
        
        // 2. If not in resources, try PIXI's Assets cache
        if (!texture) {
            for (const path of pathVariations) {
                try {
                    if (Assets.cache.has(path)) {
                        texture = Assets.get(path);
                        if (texture && texture.width > 0 && texture.height > 0) {
                            console.log(`[TextureCache] Found texture via Assets.get: ${path}`);
                            break;
                        }
                    }
                } catch (assetError) {
                    console.warn(`[TextureCache] Error checking Assets.cache for ${path}:`, assetError);
                }
            }
        }
        
        // 3. Handle Texture Not Found (Provide Fallback or Log Error)
        if (!texture || texture.width === 0 || texture.height === 0) {
            console.warn(`[TextureCache] No valid texture found for '${assetName}'. Variations tried:`, pathVariations);
            console.warn(`[TextureCache] Resources object keys:`, resources ? Object.keys(resources) : 'no resources');
            // Safely get Assets cache keys
            try {
                const assetKeys = Object.keys(Assets.cache);
                console.warn(`[TextureCache] Assets cache keys:`, assetKeys);
            } catch (error) {
                console.warn(`[TextureCache] Could not get Assets cache keys:`, error);
            }
            return Texture.WHITE; // Use PIXI's white texture as a fallback
        }

        // Cache the valid texture
        textureCache.set(assetName, texture);
        return texture;
        
    } catch (error) {
        console.error(`[TextureCache] Error loading texture for '${assetName}':`, error);
        console.error(`[TextureCache] Error stack:`, error.stack);
        return Texture.WHITE; // Fallback on error
    }
}


function getShadedColor(baseColor, height, x, y) {
    const r = (baseColor >> 16) & 0xFF;
    const g = (baseColor >> 8) & 0xFF;
    const b = baseColor & 0xFF;
    
    const heightShade = Math.floor(height * 150); // Increased contrast
    const lightX = x / MAP_WIDTH;
    const lightY = y / MAP_HEIGHT;
    const lightShade = Math.floor((1 - (lightX + lightY) / 2) * 80); // Sim top-left light
    const ambientOcclusion = Math.floor((1 - height) * 40); // Simple AO
    
    const totalShade = heightShade + lightShade - ambientOcclusion;
    
    const newR = Math.min(255, Math.max(0, r + totalShade));
    const newG = Math.min(255, Math.max(0, g + totalShade));
    const newB = Math.min(255, Math.max(0, b + totalShade));
    
    return (newR << 16) | (newG << 8) | newB;
}

function getChunkHash(x, y) {
    return `${x},${y}`;
}

// Function to get color based on height (Needed for Heightmap Legend/View)
function getHeightColor(height) {
    // Simple grayscale mapping for height
    const intensity = Math.floor(height * 255);
    const clampedIntensity = Math.max(0, Math.min(255, intensity));
    return (clampedIntensity << 16) | (clampedIntensity << 8) | clampedIntensity;
}

// Utility to generate tile texture on the fly (if needed for graphics fallback)
function getOrCreateTileTexture(type, color) {
    const key = `${type}_${color}`;
    if (tileTextureCache.has(key)) {
        return tileTextureCache.get(key);
    }
    try {
        if (!app || !app.renderer) {
            console.error("Cannot create tile texture: PIXI app not available.");
            return Texture.WHITE;
        }
        const g = new Graphics();
        g.beginFill(color);
        g.drawRect(0, 0, TILE_TEXTURE_SIZE, TILE_TEXTURE_SIZE);
        g.endFill();
        const texture = app.renderer.generateTexture(g, { 
            width: TILE_TEXTURE_SIZE, 
            height: TILE_TEXTURE_SIZE,
            scaleMode: SCALE_MODES.NEAREST // Use NEAREST for pixel art tiles
         });
        tileTextureCache.set(key, texture);
        g.destroy();
        // console.log(`Created tile texture for type ${type} with color ${color.toString(16)}`);
        return texture;
    } catch (error) {
        console.error('Error creating tile texture:', error);
        return Texture.WHITE;
    }
}

// Fix 1: Fix getEntityTypeInfo function to properly check components
// Set to track entities we've already warned about
const warnedEntities = new Set();
const debuggedEntities = new Set(); // Track entities we've already analyzed deeply

// Add a debug function to analyze problematic entities once
function debugEntityComponents(entity) {
    if (debuggedEntities.has(entity)) return;
    debuggedEntities.add(entity);
    
    // Get the actual component values, not just existence
    const plantType = Plant.type && Plant.type[entity];
    const animalType = Animal.type && Animal.type[entity];
    const rockType = Rock.type && Rock.type[entity];
    const structureType = Structure.type && Structure.type[entity];
    
    console.group(`[DEEP DEBUG] Entity #${entity} component values:`);
    console.log(`Plant.type = ${plantType}`);
    console.log(`Animal.type = ${animalType}`);
    console.log(`Rock.type = ${rockType}`);
    console.log(`Structure.type = ${structureType}`);
    
    // Check if any have valid values (> 0)
    const validComponents = [];
    if (plantType > 0) validComponents.push('Plant');
    if (animalType > 0) validComponents.push('Animal');
    if (rockType > 0) validComponents.push('Rock');
    if (structureType > 0) validComponents.push('Structure');
    
    console.log(`Valid components (type > 0): ${validComponents.join(', ') || 'NONE'}`);
    console.groupEnd();
    
    // Return true if we found the likely real component type
    return validComponents.length === 1;
}

// Add a helper function to attempt to clean up corrupted entities
export function cleanupCorruptedEntity(world, entity) {
    if (!entity) return false;
    
    // Get component values
    const plantType = Plant.type && Plant.type[entity];
    const animalType = Animal.type && Animal.type[entity];
    const rockType = Rock.type && Rock.type[entity];
    const structureType = Structure.type && Structure.type[entity];
    
    // Count valid components (type > 0)
    const validComponents = [];
    if (plantType > 0) validComponents.push('Plant');
    if (animalType > 0) validComponents.push('Animal');
    if (rockType > 0) validComponents.push('Rock');
    if (structureType > 0) validComponents.push('Structure');
    
    if (validComponents.length === 0) {
        // No valid components, nothing to clean
        return false;
    }
    
    if (validComponents.length === 1) {
        // Entity has one clear valid component, no cleanup needed
        return true;
    }
    
    // Entity is corrupted with multiple valid types
    
    // Option 1: Keep the most recently assigned component based on ID
    // (assuming higher type value means more recently assigned)
    let highestType = 0;
    let highestComponent = null;
    
    if (plantType > highestType) {
        highestType = plantType;
        highestComponent = 'Plant';
    }
    if (animalType > highestType) {
        highestType = animalType;
        highestComponent = 'Animal';
    }
    if (rockType > highestType) {
        highestType = rockType;
        highestComponent = 'Rock';
    }
    if (structureType > highestType) {
        highestType = structureType;
        highestComponent = 'Structure';
    }
    
    // Clear all components except the identified one
    if (highestComponent !== 'Plant' && plantType !== undefined) Plant.type[entity] = 0;
    if (highestComponent !== 'Animal' && animalType !== undefined) Animal.type[entity] = 0;
    if (highestComponent !== 'Rock' && rockType !== undefined) Rock.type[entity] = 0;
    if (highestComponent !== 'Structure' && structureType !== undefined) Structure.type[entity] = 0;
    
    console.log(`[CLEANUP] Entity #${entity} - Kept ${highestComponent} (value: ${highestType}), cleared others`);
    return true;
}

export function getEntityTypeInfo(world, entity) {
    if (!entity) {
        console.warn("[getEntityTypeInfo] Called with invalid entity ID");
        return { entityType: 'Unknown', typeName: 'Unknown', typeId: null, typeInfo: null };
    }
    
    // Check if this is a player entity first
    if (Player && Player[entity] !== undefined && Player[entity] > 0) {
        return { entityType: 'Player', typeName: 'Player', typeId: 1, typeInfo: null };
    }
    
    // DEBUG: Log what components this entity has
    const hasPlant = Plant.type && Plant.type[entity] !== undefined;
    const hasAnimal = Animal.type && Animal.type[entity] !== undefined;
    const hasRock = Rock.type && Rock.type[entity] !== undefined;
    const hasStructure = Structure.type && Structure.type[entity] !== undefined;
    
    // Check if entity has multiple component types - this shouldn't happen 
    // and indicates a data corruption issue
    const componentCount = [hasPlant, hasAnimal, hasRock, hasStructure]
        .filter(has => has).length;
        
    if (componentCount > 1) {
        // Only log warning once per entity ID
        if (!warnedEntities.has(entity)) {
            warnedEntities.add(entity); // Mark this entity as already warned
            const componentTypes = [
                hasPlant ? 'Plant' : '', 
                hasAnimal ? 'Animal' : '', 
                hasRock ? 'Rock' : '', 
                hasStructure ? 'Structure' : ''
            ].filter(Boolean).join(', ');
            
            console.warn(`[getEntityTypeInfo] WARNING: Entity #${entity} has ${componentCount} component types: ${componentTypes}`);
            
            // Run deep debug analysis once
            debugEntityComponents(entity);
            
            // Try to clean up the entity if it's in the problematic range
            if (entity >= 404 && entity <= 416) {
                cleanupCorruptedEntity(world, entity);
            }
        }
        
        // Check the actual type values - entity might have component arrays allocated
        // but component values might be 0 or undefined
        const plantType = Plant.type && Plant.type[entity];
        const animalType = Animal.type && Animal.type[entity];
        const rockType = Rock.type && Rock.type[entity];
        const structureType = Structure.type && Structure.type[entity];
        
        // console.log(`[DEBUG] Entity #${entity} actual component type values:`, {
        //     plantType,
        //     animalType, 
        //     rockType,
        //     structureType
        // });
        
        // Check which type has a valid value > 0
        if (plantType && plantType > 0) {
            // console.log(`[DEBUG] Entity #${entity} has valid Plant type value: ${plantType}`);
            const typeId = plantType;
            const plantTypeObj = Object.values(PLANT_TYPES).find(type => type.id === typeId);
            return { entityType: 'Plant', typeName: plantTypeObj ? plantTypeObj.name : `Unknown Plant (ID: ${typeId})`, typeId, typeInfo: plantTypeObj };
        }
        if (animalType && animalType > 0) {
            // console.log(`[DEBUG] Entity #${entity} has valid Animal type value: ${animalType}`);
            const typeId = animalType;
            const animalTypeObj = Object.values(ANIMAL_TYPES).find(type => type.id === typeId);
            return { entityType: 'Animal', typeName: animalTypeObj ? animalTypeObj.name : `Unknown Animal (ID: ${typeId})`, typeId, typeInfo: animalTypeObj };
        }
        if (rockType && rockType > 0) {
            // console.log(`[DEBUG] Entity #${entity} has valid Rock type value: ${rockType}`);
            const typeId = rockType;
            const rockTypeObj = Object.values(ROCK_TYPES).find(type => type.id === typeId);
            return { entityType: 'Rock', typeName: rockTypeObj ? rockTypeObj.name : `Unknown Rock (ID: ${typeId})`, typeId, typeInfo: rockTypeObj };
        }
        if (structureType && structureType > 0) {
            // console.log(`[DEBUG] Entity #${entity} has valid Structure type value: ${structureType}`);
            const typeId = structureType;
            const structureName = typeId === 1 ? 'Hive' : `Unknown Structure (ID: ${typeId})`;
            return { entityType: 'Structure', typeName: structureName, typeId, typeInfo: { id: typeId, name: structureName } };
        }
        
        // If no valid type value, return Unknown
        // console.warn(`[getEntityTypeInfo] Entity #${entity} has multiple component types but no valid type values`);
        return { entityType: 'Unknown', typeName: 'Unknown', typeId: null, typeInfo: null };
    }
    
    // No component confusion, proceed with normal type detection
    // We need to be more selective about detecting entity types
    // Check if the entity has valid type values in Rock component first
    if (Rock.type && Rock.type[entity] !== undefined && Rock.type[entity] > 0) {
        const typeId = Rock.type[entity];
        
        // console.log(`[DEBUG] Rock entity #${entity} has typeId: ${typeId}`);
        
        // Log the available rock types for debugging
        // console.log(`[DEBUG] Available ROCK_TYPES:`, Object.entries(ROCK_TYPES).map(([key, val]) => 
        //     `${key}: id=${val.id}, assetName=${val.assetName}`
        // ));
        
        const rockType = Object.values(ROCK_TYPES).find(type => type.id === typeId);
        
        // console.log(`[DEBUG] Found rock type:`, rockType ? 
        //     `name=${rockType.name}, assetName=${rockType.assetName}` : 'null');
            
        return { entityType: 'Rock', typeName: rockType ? rockType.name : `Unknown Rock (ID: ${typeId})`, typeId, typeInfo: rockType };
    } 
    // Check for valid Plant type
    else if (Plant.type && Plant.type[entity] !== undefined && Plant.type[entity] > 0) {
        const typeId = Plant.type[entity];
        const plantType = Object.values(PLANT_TYPES).find(type => type.id === typeId);
        return { entityType: 'Plant', typeName: plantType ? plantType.name : `Unknown Plant (ID: ${typeId})`, typeId, typeInfo: plantType };
    } 
    // Check for valid Animal type
    else if (Animal.type && Animal.type[entity] !== undefined && Animal.type[entity] > 0) {
        const typeId = Animal.type[entity];
        const animalType = Object.values(ANIMAL_TYPES).find(type => type.id === typeId);
        return { entityType: 'Animal', typeName: animalType ? animalType.name : `Unknown Animal (ID: ${typeId})`, typeId, typeInfo: animalType };
    } 
    // Check for valid Structure type
    else if (Structure.type && Structure.type[entity] !== undefined && Structure.type[entity] > 0) {
        const typeId = Structure.type[entity];
        // Example: assuming Structure type 1 is Hive
        const structureName = typeId === 1 ? 'Hive' : `Unknown Structure (ID: ${typeId})`;
        return { entityType: 'Structure', typeName: structureName, typeId, typeInfo: { id: typeId, name: structureName } }; // Provide basic typeInfo
    }
    
    // console.warn(`[getEntityTypeInfo] Unknown entity type for ID: ${entity}`);
    return { entityType: 'Unknown', typeName: 'Unknown', typeId: null, typeInfo: null };
}

// --- OOP Classes ---

class SpritePool {
    constructor() {
        this.pools = new Map(); // Map<string, Sprite[]>
    }

    getSprite(type) {
        if (!this.pools.has(type)) this.pools.set(type, []);
        const pool = this.pools.get(type);
        if (pool.length > 0) {
            const sprite = pool.pop();
            sprite.visible = true;
            sprite.filters = null; // Reset filters on reuse
            sprite.alpha = 1;
            // Reset other properties as needed
            return sprite;
        }
        // Create a new sprite if pool is empty
        const sprite = new Sprite();
        sprite.anchor.set(0.5); // Default anchor
        return sprite;
    }

    returnSprite(type, sprite) {
        if (!sprite) {
            console.warn(`[SpritePool] Attempted to return null sprite for type: ${type}`);
            return;
        }
        if (!this.pools.has(type)) this.pools.set(type, []);
        
        // Safety check before accessing sprite properties
        try {
            sprite.visible = false;
            sprite.texture = Texture.EMPTY; // Release texture reference
            if (sprite.parent) sprite.parent.removeChild(sprite); // Ensure removed from stage
            this.pools.get(type).push(sprite);
        } catch (error) {
            console.error(`[SpritePool] Error returning sprite to pool for type ${type}:`, error);
            // Don't add to pool if there was an error with the sprite
        }
    }

    clear() {
         for (const pool of this.pools.values()) {
            pool.forEach(sprite => sprite.destroy()); // Destroy sprites when clearing
        }
        this.pools.clear();
    }
}

// First fix: Make sure GRID Z-index is high enough
const Z_INDEX = {
    TILES: 0,
    ROCKS: 1,    // Rocks often appear behind other entities
    PLANTS: 2,
    CORPSES: 3,  // Optional: If corpses are rendered
    ANIMALS: 4,
    PLAYER: 10,  // Player generally above most entities in same tile row
    STRUCTURES: 15, // Structures like Hives - Increased to be above other entities
    EFFECTS: 99, // Visual effects
    GRID: 200,    // Grid lines on top of tiles - HIGHER value to ensure visibility
    HUD: 1000,   // HUD elements above everything else
};

// Fix the TileRenderer class with improved grid rendering
class TileRenderer {
    constructor(appInstance, resources) {
        this.app = appInstance;
        this.resources = resources;
        this.container = new Container();
        this.container.zIndex = Z_INDEX.TILES; // Ensure tiles are behind entities
        this.tileSprites = new Map(); // Use Map for potentially sparse tiles {tileKey: Sprite | Graphics}
        this.waterShader = new Filter(waterVertexShader, waterFragmentShader, waterShaderUniforms);
        
        // Create dedicated border container with higher z-index
        this.borderContainer = new Container(); 
        this.borderContainer.zIndex = Z_INDEX.GRID; // Higher z-index
        this.container.addChild(this.borderContainer);
        
        // Store border sprites separately
        this.borderSprites = [];
    }

    updateWaterShaderTime() {
        // Add a check to ensure uniforms are available before accessing time
        if (this.waterShader && this.waterShader.uniforms) {
            this.waterShader.uniforms.time = (this.waterShader.uniforms.time || 0) + 0.01;
        } else {
             // Optionally log a warning if uniforms are missing when expected
             // console.warn("[TileRenderer] Water shader uniforms not ready.");
        }
    }

    render(world, playerX, playerY, showBiome, showHeightmap, showTileBorders) {
        this.updateWaterShaderTime();
        
        // Clear border sprites first
        this.clearBorders();

    const currentChunkX = Math.floor(playerX / (TILE_SIZE * CHUNK_SIZE));
    const currentChunkY = Math.floor(playerY / (TILE_SIZE * CHUNK_SIZE));
        // Fetch tiles for the current view area based on player chunk and radius
        const tiles = tileManager.getTilesInChunkRing(currentChunkX, currentChunkY, CHUNK_SIZE, CHUNKS_IN_VIEW);

        const visibleTileKeys = new Set();

        for (const tile of tiles) {
            const tileKey = `${tile.x},${tile.y}`;
            visibleTileKeys.add(tileKey);
            let sprite = this.tileSprites.get(tileKey);
        let useGraphics = false;
            let texture = null;
            let color = 0xFFFFFF;

            if (showHeightmap) {
            useGraphics = true;
                // Use height directly for color in heightmap mode
                color = getHeightColor(tile.height); 
            } else if (showBiome) {
                useGraphics = true;
                const biomeColorKey = Object.keys(BIOME_COLORS)[tile.biome];
                // Use biome color + shading in biome mode
                color = getShadedColor(BIOME_COLORS[biomeColorKey] || 0xCCCCCC, tile.height, tile.x, tile.y); 
        } else {
                // Try texture-based rendering first
            if (tile.type === TILE_TYPES.WATER) {
                    texture = getCachedTexture('water', this.resources); // Use 'water' asset key
                    if (!texture || texture === Texture.WHITE) useGraphics = true;
            } else {
                    const biomeAssetKey = BIOME_ASSET_KEYS[tile.biome];
                    if (biomeAssetKey) {
                        texture = getCachedTexture(biomeAssetKey, this.resources);
                        if (!texture || texture === Texture.WHITE) useGraphics = true;
                    } else {
                        useGraphics = true; // Fallback if no asset key defined
                    }
                }

                // Fallback color if texture failed or wasn't applicable
        if (useGraphics) {
                    if (tile.type === TILE_TYPES.WATER) {
                        // Use specific water color + shading
                        color = getShadedColor(BIOME_COLORS.WATER || 0x1E90FF, tile.height, tile.x, tile.y);
            } else {
                        const biomeColorKey = Object.keys(BIOME_COLORS)[tile.biome];
                        // Use biome color + shading, default brown
                        color = getShadedColor(BIOME_COLORS[biomeColorKey] || 0x8B4513, tile.height, tile.x, tile.y); 
                    }
                }
            }

            // Create or update sprite/graphics
            if (useGraphics) {
                if (!sprite || !(sprite instanceof Graphics)) {
                    if (sprite) sprite.destroy(); // Destroy if wrong type
                    sprite = new Graphics();
                    this.tileSprites.set(tileKey, sprite);
                    this.container.addChild(sprite);
                }
                sprite.clear();
            sprite.beginFill(color);
            sprite.drawRect(0, 0, TILE_SIZE * TILE_SCALE, TILE_SIZE * TILE_SCALE);
            sprite.endFill();
                sprite.x = tile.x * TILE_SIZE * TILE_SCALE;
                sprite.y = tile.y * TILE_SIZE * TILE_SCALE;
                sprite.filters = null; // Ensure no filters on graphics
            } else { // Use Texture
            if (!sprite || !(sprite instanceof Sprite)) {
                    if (sprite) sprite.destroy();
                    sprite = new Sprite(texture);
                    this.tileSprites.set(tileKey, sprite);
                    this.container.addChild(sprite);
            } else {
                    sprite.texture = texture; // Update texture if needed
                }
                sprite.tint = getShadedColor(0xFFFFFF, tile.height, tile.x, tile.y); // Apply shading via tint
                sprite.x = tile.x * TILE_SIZE * TILE_SCALE;
                sprite.y = tile.y * TILE_SIZE * TILE_SCALE;
                sprite.width = TILE_SIZE * TILE_SCALE;
                sprite.height = TILE_SIZE * TILE_SCALE;

                // Apply water shader
            if (tile.type === TILE_TYPES.WATER) {
                    sprite.filters = [this.waterShader];
            } else {
                    sprite.filters = null;
                }
            }
        }

        // Remove sprites for tiles no longer in view
        for (const [key, sprite] of this.tileSprites.entries()) {
            if (!visibleTileKeys.has(key)) {
                this.container.removeChild(sprite);
                sprite.destroy(); // Destroy unused tile sprites
                this.tileSprites.delete(key);
            }
        }

        // Create borders if enabled - COMPLETELY NEW APPROACH
        if (showTileBorders) {
            // console.log("Adding borders for tiles");
            
            // Create tile borders - new approach using border sprites
            for (const [key, sprite] of this.tileSprites.entries()) {
                const borderSize = 2; // Border thickness
                
                // Top border
                const topBorder = new Graphics();
                topBorder.beginFill(0xFFFFFF);
                topBorder.drawRect(sprite.x, sprite.y, TILE_SIZE * TILE_SCALE, borderSize);
                topBorder.endFill();
                this.borderContainer.addChild(topBorder);
                this.borderSprites.push(topBorder);
                
                // Bottom border
                const bottomBorder = new Graphics();
                bottomBorder.beginFill(0xFFFFFF);
                bottomBorder.drawRect(sprite.x, sprite.y + TILE_SIZE * TILE_SCALE - borderSize, 
                                    TILE_SIZE * TILE_SCALE, borderSize);
                bottomBorder.endFill();
                this.borderContainer.addChild(bottomBorder);
                this.borderSprites.push(bottomBorder);
                
                // Left border
                const leftBorder = new Graphics();
                leftBorder.beginFill(0xFFFFFF);
                leftBorder.drawRect(sprite.x, sprite.y, borderSize, TILE_SIZE * TILE_SCALE);
                leftBorder.endFill();
                this.borderContainer.addChild(leftBorder);
                this.borderSprites.push(leftBorder);
                
                // Right border
                const rightBorder = new Graphics();
                rightBorder.beginFill(0xFFFFFF);
                rightBorder.drawRect(sprite.x + TILE_SIZE * TILE_SCALE - borderSize, sprite.y, 
                                   borderSize, TILE_SIZE * TILE_SCALE);
                rightBorder.endFill();
                this.borderContainer.addChild(rightBorder);
                this.borderSprites.push(rightBorder);
            }
            
            // Create chunk border - use 4 red rectangles
        const chunkWorldX = currentChunkX * CHUNK_SIZE * TILE_SIZE * TILE_SCALE;
        const chunkWorldY = currentChunkY * CHUNK_SIZE * TILE_SIZE * TILE_SCALE;
        const chunkWorldWidth = CHUNK_SIZE * TILE_SIZE * TILE_SCALE;
        const chunkWorldHeight = CHUNK_SIZE * TILE_SIZE * TILE_SCALE;
            const chunkBorderSize = 4; // Thicker border for chunks
            
            // Chunk top border
            const chunkTop = new Graphics();
            chunkTop.beginFill(0xFF0000);
            chunkTop.drawRect(chunkWorldX, chunkWorldY, chunkWorldWidth, chunkBorderSize);
            chunkTop.endFill();
            this.borderContainer.addChild(chunkTop);
            this.borderSprites.push(chunkTop);
            
            // Chunk bottom border
            const chunkBottom = new Graphics();
            chunkBottom.beginFill(0xFF0000);
            chunkBottom.drawRect(chunkWorldX, chunkWorldY + chunkWorldHeight - chunkBorderSize, 
                              chunkWorldWidth, chunkBorderSize);
            chunkBottom.endFill();
            this.borderContainer.addChild(chunkBottom);
            this.borderSprites.push(chunkBottom);
            
            // Chunk left border
            const chunkLeft = new Graphics();
            chunkLeft.beginFill(0xFF0000);
            chunkLeft.drawRect(chunkWorldX, chunkWorldY, chunkBorderSize, chunkWorldHeight);
            chunkLeft.endFill();
            this.borderContainer.addChild(chunkLeft);
            this.borderSprites.push(chunkLeft);
            
            // Chunk right border
            const chunkRight = new Graphics();
            chunkRight.beginFill(0xFF0000);
            chunkRight.drawRect(chunkWorldX + chunkWorldWidth - chunkBorderSize, chunkWorldY, 
                             chunkBorderSize, chunkWorldHeight);
            chunkRight.endFill();
            this.borderContainer.addChild(chunkRight);
            this.borderSprites.push(chunkRight);
            
            // console.log(`Created ${this.borderSprites.length} border elements`);
        }

        this.container.sortChildren(); // Sort tiles if needed (though zIndex should handle it)
    }
    
    clearBorders() {
        // Remove all border sprites from container and destroy them
        for (const border of this.borderSprites) {
            this.borderContainer.removeChild(border);
            border.destroy();
        }
        this.borderSprites = [];
    }

    cleanup() {
        this.clearBorders();
        this.container.removeChildren();
        this.tileSprites.forEach(sprite => sprite.destroy());
        this.tileSprites.clear();
        this.borderContainer.destroy();
    }
}


class EntityRenderer {
    constructor(world, entityContainer, resources, pool) {
        this.world = world;
        this.entityContainer = entityContainer; // The main container for all entities
        this.resources = resources;
        this.pool = pool;
        this.sprites = new Map(); // Map<eid, Sprite> for all entity types
        this.app = app; // Store reference to the global app instance
    }

    // Apply selection outline to a sprite
    applyOutline(sprite, isSelected) {
        if (isSelected) {
            // Add outline filter if not already present
            if (!sprite.filters || !(sprite.filters[0] instanceof OutlineFilter)) {
                console.log(`[EntityRenderer] Creating new outline filter`);
                sprite.filters = [new OutlineFilter(4, 0x00FF00, 0.8)]; // Thicker green outline (4px)
            }
            sprite.zIndex += 100; // Boost selected entity to render on top
        } else {
            // Remove outline filter
            sprite.filters = null;
        }
    }
    
    // Renders a batch of entities of potentially mixed types
    render(entitiesToRender, lightingRadius, playerX, playerY, selectedEntity) {
        const activeSpriteIds = new Set();
        
        // NEW: Adopt any sprites that exist in world.sprites but not in our tracking
        if (this.world.sprites) {
            for (const [eidStr, sprite] of Object.entries(this.world.sprites)) {
                const eid = parseInt(eidStr, 10);
                if (!isNaN(eid) && !this.sprites.has(eid) && sprite && sprite.parent) {
                    // Skip player entities - they're handled by PlayerRenderer
                    if (Player && Player[eid] !== undefined) {
                        // console.log(`[EntityRenderer] Skipping player entity #${eid} - handled by PlayerRenderer`);
                        continue;
                    }
                    
                    // console.log(`[EntityRenderer] Adopting existing sprite for entity #${eid}`);
                    this.sprites.set(eid, sprite);
                    
                    // Make sure it's in the right container
                    if (sprite.parent !== this.entityContainer) {
                        // console.log(`[EntityRenderer] Moving sprite for entity #${eid} to the correct container`);
                        sprite.parent.removeChild(sprite);
                        this.entityContainer.addChild(sprite);
                    }
                    
                    // Mark it as active for this frame
                    activeSpriteIds.add(eid);
                }
            }
        }

        // Calculate the range of chunks currently in view
        const playerChunkX = Math.floor(playerX / (TILE_SIZE * CHUNK_SIZE));
        const playerChunkY = Math.floor(playerY / (TILE_SIZE * CHUNK_SIZE));
        const chunkRadius = CHUNKS_IN_VIEW; // Use the config value
        const minChunkX = playerChunkX - chunkRadius;
        const maxChunkX = playerChunkX + chunkRadius;
        const minChunkY = playerChunkY - chunkRadius;
        const maxChunkY = playerChunkY + chunkRadius;

        for (const eid of entitiesToRender) {
            activeSpriteIds.add(eid); // Mark this sprite as active for this frame

            const entityX = Position.x[eid];
            const entityY = Position.y[eid];

            // --- Chunk Culling ---
            const entityChunkX = Math.floor(entityX / (TILE_SIZE * CHUNK_SIZE));
            const entityChunkY = Math.floor(entityY / (TILE_SIZE * CHUNK_SIZE));
            if (entityChunkX < minChunkX || entityChunkX > maxChunkX || entityChunkY < minChunkY || entityChunkY > maxChunkY) {
                // Entity is outside the currently rendered chunks, ensure sprite is hidden/pooled
                if (this.sprites.has(eid)) {
                    const sprite = this.sprites.get(eid);
                    const { entityType } = getEntityTypeInfo(this.world, eid); // Get type for pooling
                    this.pool.returnSprite(entityType.toLowerCase(), sprite); 
                    this.sprites.delete(eid); 
                }
                continue; // Skip rendering this entity
            }

            // --- Lighting Culling ---
            const dx = entityX - playerX;
            const dy = entityY - playerY;
            const distSq = dx * dx + dy * dy; // Use squared distance
            if (distSq > lightingRadius * lightingRadius) {
                // Entity is outside lighting radius, ensure sprite is hidden/pooled
                if (this.sprites.has(eid)) {
                    const sprite = this.sprites.get(eid);
                    const { entityType } = getEntityTypeInfo(this.world, eid);
                    this.pool.returnSprite(entityType.toLowerCase(), sprite); // Return to pool by type
                    this.sprites.delete(eid); // Remove from active sprites
                }
                continue; // Skip rendering this entity
            }

            // --- Get or Create Sprite ---
            let sprite = this.sprites.get(eid);
            const { entityType, typeName, typeId, typeInfo } = getEntityTypeInfo(this.world, eid);
            const poolKey = entityType.toLowerCase();

            if (!sprite) {
                // NEW: Check if this entity already has a custom sprite in world.sprites
                if (entityType === 'Structure' && this.world.sprites && this.world.sprites[eid]) {
                    // console.log(`[EntityRenderer] Using existing custom sprite for Structure entity #${eid}`);
                    sprite = this.world.sprites[eid]; // Assign directly
                    
                    // Ensure it's valid and track it
                    if (sprite && sprite.anchor) { // Check if it's a valid sprite with an anchor
                        this.sprites.set(eid, sprite);
                        // Make sure it's in the right container
                        if (sprite.parent !== this.entityContainer) {
                            if (sprite.parent) sprite.parent.removeChild(sprite);
                            this.entityContainer.addChild(sprite);
                        }
                    } else {
                         console.warn(`[EntityRenderer] Invalid or missing custom sprite found for Structure #${eid}. Will try pool. Sprite:`, sprite);
                         sprite = null; // Reset sprite so pool is used below
                    }
                }
                
                // If sprite is still null (either wasn't custom or custom failed), get from pool
                if (!sprite) {
                    sprite = this.pool.getSprite(poolKey);
                    if (sprite) { // Check if pool returned a valid sprite
                        this.sprites.set(eid, sprite);
                        this.entityContainer.addChild(sprite);
                        // Check if sprite.anchor exists before setting it
                        if (sprite.anchor) {
                            sprite.anchor.set(0.5); 
                        } else {
                            console.error(`[EntityRenderer] Pooled sprite for ${poolKey} has no anchor! Returning to pool. Sprite:`, sprite);
                            // Potentially handle this error - maybe return sprite to pool?
                            this.entityContainer.removeChild(sprite);
                            this.sprites.delete(eid);
                            this.pool.returnSprite(poolKey, sprite);
                            sprite = null; // Ensure we don't proceed with this invalid sprite
                        }
                    } else {
                        console.error(`[EntityRenderer] Failed to get sprite from pool for key: ${poolKey}. Skipping entity ${eid}.`);
                        // Cannot render this entity if no sprite could be obtained
                         continue; // Skip to next entity
                    }
                }

                // --- Configure Sprite ---
                // This section now only runs if sprite is guaranteed to be valid
                if (sprite) { 
                    let assetName = null;
                    let baseSize = TILE_SIZE * TILE_SCALE; // Default size
                    let specificSizeMultiplier = 1.0;

                    if (typeInfo) {
                        assetName = typeInfo.assetName || typeName?.toLowerCase(); // Fallback to lowercase name
                        if (entityType === 'Plant') {
                             // Use the size directly from typeInfo
                             specificSizeMultiplier = typeInfo.size || 1.0;
                        } else if (entityType === 'Animal') {
                            // Apply animal size multipliers if defined in ANIMAL_TYPES or ANIMAL_SIZES
                             specificSizeMultiplier = typeInfo.size || ANIMAL_SIZES[typeName] || 0.4; // Example fallback
                             
                             // Special handling for rabbits
                             if (typeName === 'RABBIT' || typeName === 'RABBIT_WILD' || typeName === 'RABBIT_WHITE') {
                                 assetName = 'rabbit';
                                 specificSizeMultiplier = 0.35; // 35% of tile size for rabbits
                             }
                        } else if (entityType === 'Rock') {
                             // Rocks might scale based on their 'size' component
                             specificSizeMultiplier = Rock.size[eid] || 1.0;
                        } else if (entityType === 'Structure' && typeId === 1) { // Hive specific
                             // We already handled getting the sprite above. Just set size multiplier.
                             specificSizeMultiplier = 0.8; // 80% size for structures
                             // assetName = 'hive'; // Don't override assetName if custom sprite exists
                        }
                    }

                    if (assetName) {
                         const texture = getCachedTexture(assetName, this.resources);
                         if (!texture || texture === Texture.WHITE) {
                             console.warn(`[EntityRenderer] Failed to get texture for ${entityType} ${typeName} (Asset: ${assetName}). Using fallback.`);
                             
                             // Create a simple fallback graphic if texture fails
                             const fallbackGraphics = new Graphics();
                             
                             // Different fallback visuals based on entity type
                             if (entityType === 'Plant') {
                                 fallbackGraphics.beginFill(0x00FF00, 0.7); // Green for plants
                                 fallbackGraphics.drawCircle(0, 0, baseSize * specificSizeMultiplier * 0.4);
                             } else if (entityType === 'Animal') {
                                 fallbackGraphics.beginFill(0xFF0000, 0.7); // Red for animals
                                 fallbackGraphics.drawCircle(0, 0, baseSize * specificSizeMultiplier * 0.4);
                             } else if (entityType === 'Rock') {
                                 fallbackGraphics.beginFill(0x808080, 0.8); // Gray for rocks
                                 fallbackGraphics.drawRect(-baseSize * specificSizeMultiplier * 0.3,
                                      -baseSize * specificSizeMultiplier * 0.3,
                                      baseSize * specificSizeMultiplier * 0.6,
                                      baseSize * specificSizeMultiplier * 0.6);
                            } else {
                                 fallbackGraphics.beginFill(0x808080, 0.7); // Default gray
                                 fallbackGraphics.drawCircle(0, 0, baseSize * specificSizeMultiplier * 0.4);
                             }
                             
                             fallbackGraphics.endFill();
                             
                             // Fix: Use the global app instance if this.app is undefined
                             let renderer = (this.app && this.app.renderer) ? this.app.renderer : (window.app && window.app.renderer);
                             if (renderer) {
                                 sprite.texture = renderer.generateTexture(fallbackGraphics);
                            } else {
                                 console.error("[EntityRenderer] No app instance available for texture generation");
                                 sprite.texture = Texture.WHITE;
                            }
                             fallbackGraphics.destroy();
                        } else {
                            sprite.texture = texture;
                         }
                    } else if (!(entityType === 'Structure' && typeId === 1 && this.world.sprites[eid])) {
                         // Don't generate fallback if it was a custom sprite structure (handled above)
                         console.warn(`[EntityRenderer] No asset name or special handling for ${entityType} ${typeName}. Using default fallback.`);
                        
                        // Different fallback visuals based on entity type
                        const fallbackGraphics = new Graphics();
                        
                        if (entityType === 'Rock') {
                            fallbackGraphics.beginFill(0x808080, 0.9); // Gray for rocks
                            fallbackGraphics.drawRect(-baseSize * specificSizeMultiplier * 0.3,
                                -baseSize * specificSizeMultiplier * 0.3,
                                baseSize * specificSizeMultiplier * 0.6,
                                baseSize * specificSizeMultiplier * 0.6);
                        } else {
                            fallbackGraphics.beginFill(0xCCCCCC, 0.7); // Light gray for unknown
                            fallbackGraphics.drawCircle(0, 0, baseSize * specificSizeMultiplier * 0.4);
                        }
                        
                        fallbackGraphics.endFill();
                        
                        // Generate texture from graphics
                        let renderer = (this.app && this.app.renderer) ? this.app.renderer : (window.app && window.app.renderer);
                        if (renderer) {
                            sprite.texture = renderer.generateTexture(fallbackGraphics);
                        } else {
                            sprite.texture = Texture.WHITE;
                        }
                        
                        fallbackGraphics.destroy();
                        specificSizeMultiplier = 0.5; // Make unknown things small
                    }
                     
                    // Set size only if sprite is valid and has a texture
                    if (sprite.texture) { 
                         sprite.width = baseSize * specificSizeMultiplier;
                         sprite.height = baseSize * specificSizeMultiplier;
                    } else {
                         console.warn(`[EntityRenderer] No texture set for sprite ${eid}, cannot set size.`);
                    }
                 } // End of configuration block

            } // End of if (!sprite) block
            
            // --- FINAL CHECK ---
            // Before proceeding to update properties, ensure sprite is valid
            if (!sprite || !sprite.anchor) { // Check anchor specifically as that was the error source
                 console.error(`[EntityRenderer] Invalid sprite state for entity #${eid} (${entityType} ${typeName}) before property update. Skipping render. Sprite:`, sprite);
                 // Attempt cleanup if possible
                 if (sprite && this.sprites.has(eid)) {
                     // Check if sprite has a parent before removing
                     if (sprite.parent) {
                        this.pool.returnSprite(poolKey, sprite); // Should handle removal from parent
                     }
                     this.sprites.delete(eid);
                 } else if (sprite && !this.sprites.has(eid)) {
                     // Sprite exists but isn't tracked, maybe it's the custom one that failed validation?
                     if (sprite.destroy) sprite.destroy(); // Clean up loose sprite? Risky.
                 }
                 continue; // Skip to next entity
            }

            // --- Update Sprite Properties ---
            // Now we can be more confident sprite is valid
            sprite.position.set(entityX, entityY);
            sprite.visible = true; // Ensure visibility if it was pooled

            // --- Consistent Z-index based on Y position with type offsets ---
            const baseZ = Math.floor(entityY / TILE_SIZE);
            
            // Calculate z-index with type-specific offsets
            // Using 1000 multiplier to ensure clear layer separation
            let zIndexOffset = 0;
            switch (entityType) {
                case 'Rock':
                    zIndexOffset = Z_INDEX.ROCKS;
                    break;
                case 'Plant':
                    zIndexOffset = Z_INDEX.PLANTS;
                    break;
                case 'Animal':
                    zIndexOffset = Z_INDEX.ANIMALS;
                    break;
                case 'Structure':
                    zIndexOffset = Z_INDEX.STRUCTURES;
                    break;
                case 'Corpse':
                    zIndexOffset = Z_INDEX.CORPSES;
                    break;
                default:
                    zIndexOffset = 0;
            }
            
            sprite.zIndex = (baseZ * 1000) + zIndexOffset;
            
            // Apply selection outline
             if (eid === selectedEntity) {
                 console.log(`[EntityRenderer] Applying selection outline to entity #${eid}, entityType=${entityType}`);
                 
                 // Special handling for custom Structure sprites that might be managed differently
                 if (entityType === 'Structure' && this.world.sprites && this.world.sprites[eid] === sprite) {
                     console.log(`[EntityRenderer] Applying outline to custom Structure sprite`);
                 }
                 
                 this.applyOutline(sprite, true);
                 // Boost selected entity to render on top - add 500 to ensure it's above everything else
                 sprite.zIndex += 500;
    } else {
                 this.applyOutline(sprite, false);
             }
        }

        // --- Cleanup Unused Sprites ---
        // Iterate through the sprites currently managed by this renderer
        for (const [eid, sprite] of this.sprites.entries()) {
            // If a sprite's entity ID wasn't in the active set for this frame...
            if (!activeSpriteIds.has(eid)) {
                const { entityType } = getEntityTypeInfo(this.world, eid);
                
                // Skip custom sprites for Structure entities
                if (entityType === 'Structure' && this.world.sprites && this.world.sprites[eid] === sprite) {
                    // console.log(`[EntityRenderer] Preserving custom sprite for Structure entity #${eid}`);
                    this.sprites.delete(eid); // Just remove from our tracking collection
                } else {
                    this.pool.returnSprite(entityType.toLowerCase(), sprite); // Return it to the pool
                    this.sprites.delete(eid); // Remove it from the active map
                }
            }
        }
    }

    // No need for individual cleanup methods per type anymore
    cleanupAll() {
        console.log(`[EntityRenderer] Cleaning up ${this.sprites.size} sprites.`);
        for (const [eid, sprite] of this.sprites.entries()) {
            try {
                if (!sprite) {
                    console.warn(`[EntityRenderer] Null sprite found for entity #${eid}, skipping.`);
                    continue;
                }
                
                // Remove from parent first if it has one
                if (sprite.parent) {
                    sprite.parent.removeChild(sprite);
                }
                
                // Get entity type carefully, defaulting to 'unknown' if there's an issue
                let entityType = 'unknown';
                try {
                    const typeInfo = getEntityTypeInfo(this.world, eid);
                    entityType = typeInfo.entityType.toLowerCase();
                } catch (error) {
                    console.error(`[EntityRenderer] Error getting entity type for #${eid}:`, error);
                }
                
                // Return to pool
                this.pool.returnSprite(entityType, sprite);
            } catch (error) {
                console.error(`[EntityRenderer] Error cleaning up sprite for entity #${eid}:`, error);
            }
        }
        this.sprites.clear();
    }
}


class PlayerRenderer {
    constructor(world, container, resources) {
        this.world = world;
        this.container = container;
        this.resources = resources;
        this.sprite = null;
        this.playerId = null;
        // Define atlas data directly here or load from JSON
        this.playerAtlasData = {
             frames: {
                 "player_down_0": { frame: { x: 0, y: 0, w: 16, h: 32 } },
                 "player_down_1": { frame: { x: 16, y: 0, w: 16, h: 32 } },
                 "player_down_2": { frame: { x: 32, y: 0, w: 16, h: 32 } },
                 "player_right_0": { frame: { x: 0, y: 32, w: 16, h: 32 } },
                 "player_right_1": { frame: { x: 16, y: 32, w: 16, h: 32 } },
                 "player_right_2": { frame: { x: 32, y: 32, w: 16, h: 32 } },
                 "player_up_0": { frame: { x: 0, y: 64, w: 16, h: 32 } },
                 "player_up_1": { frame: { x: 16, y: 64, w: 16, h: 32 } },
                 "player_up_2": { frame: { x: 32, y: 64, w: 16, h: 32 } },
                 // Add left frames if they exist in the sheet, otherwise we'll flip 'right'
             },
             meta: {
                 image: "assets/images/player/player-spritemap.png", // Ensure this path is correct
                 size: { w: 64, h: 96 }, // Check actual image dimensions
                 scale: "1"
             },
             animations: {
                 down: ["player_down_0", "player_down_1", "player_down_2"],
                 right: ["player_right_0", "player_right_1", "player_right_2"],
                 up: ["player_up_0", "player_up_1", "player_up_2"]
                 // Add 'left' if separate frames exist: left: ["player_left_0", ...]
             }
         };
    }

    async preload() {
        if (!playerSpritesheet) {
            try {
                 // Ensure the base texture is loaded *before* creating the Spritesheet
                const baseTexture = await Assets.load(this.playerAtlasData.meta.image);
                 if (!baseTexture) {
                     throw new Error(`Player spritesheet base texture not loaded: ${this.playerAtlasData.meta.image}`);
                 }
                 playerSpritesheet = new Spritesheet(baseTexture, this.playerAtlasData);
                 await playerSpritesheet.parse();
                 playerSpritesheetReady = true;
                 console.log('[PlayerRenderer] Player spritesheet parsed successfully.');
    } catch (error) {
                console.error('[PlayerRenderer] Failed to preload or parse player spritesheet:', error);
                playerSpritesheetReady = false;
            }
        }
    }

    ensureSpriteExists(playerId) {
        if (this.sprite && this.playerId === playerId) {
            return true; // Sprite already exists for the correct player
        }

        if (this.sprite) { // Mismatched player ID, destroy old sprite
            this.sprite.destroy();
            this.sprite = null;
        }

        this.playerId = playerId;

        if (playerSpritesheetReady && playerSpritesheet.animations.down) {
            this.sprite = new AnimatedSprite(playerSpritesheet.animations.down);
            this.sprite.animationSpeed = 0.15;
            this.sprite.play();
            console.log('[PlayerRenderer] Created AnimatedSprite for player.');
        } else {
            // Fallback if spritesheet failed
            console.warn('[PlayerRenderer] Spritesheet not ready. Creating fallback graphic.');
            const fallbackGraphics = new Graphics();
            fallbackGraphics.beginFill(0x00FF00); // Green circle
            fallbackGraphics.drawCircle(0, 0, PLAYER_SIZE * TILE_SCALE * 0.4);
            fallbackGraphics.endFill();
            const fallbackTexture = app.renderer.generateTexture(fallbackGraphics); // Use global 'app'
            fallbackGraphics.destroy();
            this.sprite = new Sprite(fallbackTexture);
        }

        this.sprite.anchor.set(0.5, 1.0); // Anchor at bottom center
        const scale = (PLAYER_SIZE * TILE_SCALE) / this.sprite.texture.height; // Scale based on desired pixel height
        this.sprite.scale.set(scale);
        
        // Set initial z-index - use a default value, will be updated in render() method
        this.sprite.zIndex = Z_INDEX.PLAYER;
        
        // Remove any filters or effects that might cause white circles or outlines
        this.sprite.filters = null;
        this.sprite.mask = null;
        
        this.container.addChild(this.sprite);
        this.world.sprites = this.world.sprites || {}; // Ensure sprites object exists
        this.world.sprites[playerId] = this.sprite; // Store reference in world (if needed elsewhere)
        return true;
    }

    render(playerX, playerY, velocityX, velocityY) {
        if (!this.sprite || this.playerId === null) {
             console.warn("[PlayerRenderer] Attempted to render player before sprite creation or without player ID.");
             return; // Cannot render without a sprite or player ID
        }

        // Update position
        this.sprite.position.set(playerX, playerY);
        
        // --- Fixed Z-index calculation for proper layering ---
        // Use the same system as entities but with a player offset
        const baseZ = Math.floor(playerY / TILE_SIZE);
        
        // Add player-specific offset to ensure player renders above entities at same y-position
        // The multiplier (1000) creates clear separation between layers
        // The offset (50) ensures player is above other entities but below structures
        this.sprite.zIndex = (baseZ * 1000) + Z_INDEX.PLAYER;

        // Determine direction and animation
        let currentAnimation = 'down'; // Default
        let isMoving = Math.abs(velocityX) > 0.1 || Math.abs(velocityY) > 0.1;

        if (Math.abs(velocityX) > Math.abs(velocityY)) {
            currentAnimation = 'right'; // Use 'right' for both left/right, flip scale for left
        } else if (velocityY < -0.1) {
            currentAnimation = 'up';
        } else if (velocityY > 0.1) {
            currentAnimation = 'down';
        } // If velocityY is near zero, stick with horizontal or default down

        // Flip sprite scale for left movement
        if (currentAnimation === 'right' && velocityX < 0) {
            this.sprite.scale.x = -Math.abs(this.sprite.scale.x);
        } else if (currentAnimation === 'right') {
            this.sprite.scale.x = Math.abs(this.sprite.scale.x);
        } else {
            this.sprite.scale.x = Math.abs(this.sprite.scale.x);
        }

        // Update animation state only if using AnimatedSprite and spritesheet is ready
        if (this.sprite instanceof AnimatedSprite && playerSpritesheetReady) {
            // Play/Stop
            if (isMoving && !this.sprite.playing) {
                this.sprite.play();
            } else if (!isMoving && this.sprite.playing) {
                this.sprite.stop();
                this.sprite.currentFrame = 0; // Reset to first frame when stopping
            }

            // Change animation sequence if different
            const targetAnimation = playerSpritesheet.animations[currentAnimation];
            if (targetAnimation && this.sprite.textures !== targetAnimation) {
                 this.sprite.textures = targetAnimation;
                 if (isMoving) this.sprite.play(); // Ensure it plays if moving
                 // console.log(`[PlayerRenderer] Switched animation to: ${currentAnimation}`);
            }
        }
         this.sprite.visible = true; // Ensure player is visible
    }

    cleanup() {
        if (this.sprite) {
            this.sprite.destroy();
            this.sprite = null;
        }
        this.playerId = null;
    }
}


class MinimapRenderer {
    constructor(parentContainer, world) {
        this.parentContainer = parentContainer;
        this.world = world;
        this.container = new Container();
        this.container.visible = false; // Hidden by default
        this.config = {
            width: 180, // Inner map size
            height: 180,
            padding: 10,
            totalWidth: 200,
            totalHeight: 200,
            x: window.innerWidth - 210,
            y: 10,
            updateInterval: 500, // ms
            downsamplingFactor: Math.max(1, Math.floor(Math.sqrt(MAP_WIDTH * MAP_HEIGHT) / 150)), // Adjust for performance
            plantDotScale: 0.3,
            playerDotSize: 3,
        };
        this.needsRedraw = true; // Flag to redraw map background
        this.lastUpdateTime = 0;
        this.mapGraphics = new Graphics(); // For drawing tiles/plants
        this.playerDot = new Graphics();
        this.plantQuery = defineQuery([Plant, Position]); // Query for plants

        this.createElements();
        this.parentContainer.addChild(this.container);
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    createElements() {
        // Background
        const bg = new Graphics();
        bg.beginFill(0x000000, 0.7);
        bg.drawRoundedRect(0, 0, this.config.totalWidth, this.config.totalHeight, 10);
        bg.endFill();
        this.container.addChild(bg);

        // Map drawing area
        this.mapGraphics.position.set(this.config.padding, this.config.padding + 10); // Offset for title
        this.container.addChild(this.mapGraphics);

        // Player dot
        this.playerDot.beginFill(0xFF0000); // Red dot
        this.playerDot.drawCircle(0, 0, this.config.playerDotSize);
        this.playerDot.endFill();
        this.container.addChild(this.playerDot); // Add dot directly to container, its position will be updated

        // Title
        const title = new Text('Minimap', { fontFamily: 'Arial', fontSize: 12, fill: 0xFFFFFF });
        title.position.set(this.config.padding, this.config.padding / 2);
        this.container.addChild(title);

        // Initial position
        this.container.position.set(this.config.x, this.config.y);
    }

    handleResize() {
        this.config.x = window.innerWidth - (this.config.totalWidth + this.config.padding);
        this.container.position.x = this.config.x;
    }

    setNeedsRedraw() {
        this.needsRedraw = true;
    }

    render(playerX, playerY, showMinimap) {
        this.container.visible = showMinimap;
        if (!showMinimap) return;

        const now = performance.now();
        if (now - this.lastUpdateTime < this.config.updateInterval && !this.needsRedraw) {
            // Only update player dot if no redraw needed and interval hasn't passed
            this.updatePlayerDot(playerX, playerY);
        return;
    }
    
        this.lastUpdateTime = now;

        if (this.needsRedraw) {
            this.redrawMapBackground();
            this.needsRedraw = false; // Reset flag after redraw
        }

        this.updatePlayerDot(playerX, playerY);
    }

    redrawMapBackground() {
        this.mapGraphics.clear();
        const scale = Math.min(this.config.width / MAP_WIDTH, this.config.height / MAP_HEIGHT);
        const step = this.config.downsamplingFactor;

        // --- Draw Tiles ---
        const tileColors = new Map(); // Group drawing by color for efficiency
        for (let x = 0; x < MAP_WIDTH; x += step) {
            for (let y = 0; y < MAP_HEIGHT; y += step) {
                const tile = tileManager.getTile(x, y); // Assumes tileManager is accessible
                if (tile) {
                    let color;
                    if (tile.type === TILE_TYPES.WATER) {
                        color = BIOME_COLORS.WATER;
    } else {
                        const biomeKey = Object.keys(BIOME_COLORS)[tile.biome];
                        color = BIOME_COLORS[biomeKey] || 0x8B4513; // Default brown
                    }
                    if (!tileColors.has(color)) tileColors.set(color, []);
                    tileColors.get(color).push({ x: x * scale, y: y * scale, w: scale * step, h: scale * step });
                }
            }
        }
        // Batch draw by color
        for (const [color, rects] of tileColors) {
            this.mapGraphics.beginFill(color);
            rects.forEach(r => this.mapGraphics.drawRect(r.x, r.y, r.w, r.h));
            this.mapGraphics.endFill();
        }


        // --- Draw Plants (Simplified dots) ---
        const plants = this.plantQuery(this.world);
        const plantPositions = new Map(); // Avoid overdraw in downsampled areas

        // Group drawing by color
        const plantColors = new Map();
        const plantDotSize = scale * step * this.config.plantDotScale; // Base dot size on scaled step

        for (const eid of plants) {
             const plantX = Position.x[eid];
             const plantY = Position.y[eid];
             const tileX = Math.floor(plantX / TILE_SIZE);
             const tileY = Math.floor(plantY / TILE_SIZE);

             const key = `${Math.floor(tileX / step)},${Math.floor(tileY / step)}`;
            if (plantPositions.has(key)) continue;
            plantPositions.set(key, true);
            
             const typeId = Plant.type[eid];
             let pColor = 0x00FF00; // Default green
             // Add specific plant colors if desired
             if (typeId === PLANT_TYPES.FLOWER.id) pColor = 0xFF69B4;
             else if (typeId === PLANT_TYPES.MUSHROOM.id) pColor = 0x8B4513;

             if (!plantColors.has(pColor)) plantColors.set(pColor, []);
             plantColors.get(pColor).push({ x: tileX * scale + (scale*step/2), y: tileY * scale + (scale*step/2) });
        }
         // Batch draw plants by color
         for (const [color, positions] of plantColors) {
             this.mapGraphics.beginFill(color);
             positions.forEach(p => this.mapGraphics.drawCircle(p.x, p.y, plantDotSize));
             this.mapGraphics.endFill();
         }
    }

    updatePlayerDot(playerX, playerY) {
        const scale = Math.min(this.config.width / MAP_WIDTH, this.config.height / MAP_HEIGHT);
        const playerTileX = Math.floor(playerX / TILE_SIZE);
        const playerTileY = Math.floor(playerY / TILE_SIZE);

        // Position relative to mapGraphics container
        const dotX = this.config.padding + (playerTileX * scale) + (scale / 2);
        const dotY = this.config.padding + 10 + (playerTileY * scale) + (scale / 2); // Offset by mapGraphics Y pos

        this.playerDot.position.set(dotX, dotY);
    }

    cleanup() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        this.container.destroy({ children: true }); // Clean up PIXI objects
    }
}


class EntityInfoPanel {
    constructor(parentContainer) {
        this.parentContainer = parentContainer;
        this.container = new Container();
        this.container.visible = false;
        this.config = {
            width: 300,
            height: 280,
            padding: 12,
            lineHeight: 16,
            x: 12,
            y: 56, // Position below FPS counter
        };

        // Pixel art inspired text styles
        this.titleStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 14,
            fontWeight: 'normal',
            fill: 0x00FFFF, // Cyan for titles - classic terminal style
            letterSpacing: 1,
            align: 'left'
        });

        this.textStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 10,
            fontWeight: 'normal',
            fill: 0x00FF00, // Classic green for text
            wordWrap: true,
            wordWrapWidth: this.config.width - this.config.padding * 2,
            lineHeight: this.config.lineHeight,
            align: 'left',
            letterSpacing: 0.5
        });

        this.background = new Graphics();
        this.titleField = new Text('', this.titleStyle);
        this.textField = new Text('', this.textStyle);

        this.createElements();
        this.parentContainer.addChild(this.container);
    }

    createElements() {
        // Retro terminal background with pixel styling
        const bg = this.background;
        
        // Main background - dark terminal
        bg.fill({ color: 0x0A0A0A });
        bg.rect(0, 0, this.config.width, this.config.height);
        
        // Pixel border pattern
        bg.setStrokeStyle({ width: 2, color: 0x00FFFF });
        bg.rect(0, 0, this.config.width, this.config.height);
        
        // Inner border for depth
        bg.setStrokeStyle({ width: 1, color: 0x008888 });
        bg.rect(2, 2, this.config.width - 4, this.config.height - 4);
        
        // Corner pixel decorations
        const cornerSize = 8;
        // Top-left corner
        bg.fill({ color: 0x00FFFF });
        bg.rect(0, 0, cornerSize, 2);
        bg.rect(0, 0, 2, cornerSize);
        // Top-right corner
        bg.fill({ color: 0x00FFFF });
        bg.rect(this.config.width - cornerSize, 0, cornerSize, 2);
        bg.rect(this.config.width - 2, 0, 2, cornerSize);
        // Bottom-left corner
        bg.fill({ color: 0x00FFFF });
        bg.rect(0, this.config.height - 2, cornerSize, 2);
        bg.rect(0, this.config.height - cornerSize, 2, cornerSize);
        // Bottom-right corner
        bg.fill({ color: 0x00FFFF });
        bg.rect(this.config.width - cornerSize, this.config.height - 2, cornerSize, 2);
        bg.rect(this.config.width - 2, this.config.height - cornerSize, 2, cornerSize);
        
        // Scan line effect
        for (let i = 0; i < this.config.height; i += 6) {
            bg.fill({ color: 0x00FF00, alpha: 0.05 });
            bg.rect(4, i, this.config.width - 8, 1);
        }
        
        this.container.addChild(bg);

        // Title field
        this.titleField.position.set(this.config.padding, this.config.padding);
        this.container.addChild(this.titleField);

        // Text field
        this.textField.position.set(this.config.padding, this.config.padding + 20);
        this.container.addChild(this.textField);

        // Initial position
        this.container.position.set(this.config.x, this.config.y);
    }

     update(world, selectedEntity, selectedEntityType) {
        if (!selectedEntity || !selectedEntityType) {
            this.container.visible = false;
            return;
        }

        let title = '';
        let info = '';
        try {
            const entityId = selectedEntity;
            
            // Get detailed entity info
            const { entityType, typeName, typeId, typeInfo } = getEntityTypeInfo(world, entityId);

            if (!typeInfo && selectedEntityType.toLowerCase() === 'structure') {
                // Special handling for structures
                const structureTypeId = Structure.type ? Structure.type[entityId] : 1;
                const structureName = structureTypeId === 1 ? 'Hive' : `Structure (ID: ${structureTypeId})`;
                
                title = structureName;
                
                // Basic position info
                const posX = Position.x[entityId]?.toFixed(1) ?? 'N/A';
                const posY = Position.y[entityId]?.toFixed(1) ?? 'N/A';
                info = `Position: (${posX}, ${posY})\n` +
                       `Entity ID: ${entityId}\n` +
                       `Type ID: ${structureTypeId}\n` +
                       `━━━━━━━━━━━━━━━━━━━━\n`;
            } else if (typeInfo) {
                // Standard entity info
                title = typeName || entityType;
                
                // Position info
                const posX = Position.x[entityId]?.toFixed(1) ?? 'N/A';
                const posY = Position.y[entityId]?.toFixed(1) ?? 'N/A';
                info = `Position: (${posX}, ${posY})\n` +
                       `Entity ID: ${entityId}\n` +
                       `Type: ${typeName}\n` +
                       `━━━━━━━━━━━━━━━━━━━━\n`;
                
                // Add type-specific info
                if (selectedEntityType === 'Animal' && Animal.energy) {
                    const energy = Animal.energy[entityId]?.toFixed(1) ?? 'N/A';
                    const age = Animal.age[entityId] ?? 'N/A';
                    info += `Energy: ${energy}\n` +
                           `Age: ${age}\n`;
                } else if (selectedEntityType === 'Plant' && Plant.growth) {
                    const growth = Plant.growth[entityId]?.toFixed(2) ?? 'N/A';
                    const age = Plant.age[entityId] ?? 'N/A';
                    info += `Growth: ${(growth * 100).toFixed(0)}%\n` +
                           `Age: ${age}\n`;
                }
            } else {
                title = 'Unknown Entity';
                info = `Entity ID: ${entityId}\n` +
                       `Type: ${selectedEntityType}\n` +
                       `━━━━━━━━━━━━━━━━━━━━\n`;
            }

            // Update title and text fields
            this.titleField.text = title;
            this.textField.text = info;
            this.container.visible = true;

        } catch (error) {
            console.error('[EntityInfoPanel] Error updating entity info:', error);
            this.titleField.text = 'Error';
            this.textField.text = 'Failed to load entity information';
            this.container.visible = true;
        }
    }
    
    cleanup() {
        this.container.destroy({ children: true });
    }
}


class DebugOverlayPanel {
     constructor(parentContainer, world) {
        this.parentContainer = parentContainer;
        this.world = world;
        this.container = new Container();
        this.container.visible = false; // Hidden by default
        this.config = {
            width: 300,
            height: 450, // Adjust as needed
            padding: 10,
            lineHeight: 14,
            x: 10,
            y: 10, // Top-left corner
        };
        this.debugVisible = false;

        this.textStyle = new TextStyle({
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: 10,
            fill: 0xDDDDDD, // Lighter gray
            wordWrap: true,
            wordWrapWidth: this.config.width - this.config.padding * 2,
            lineHeight: this.config.lineHeight,
        });

        this.background = new Graphics();
        this.textField = new Text('', this.textStyle);

        this.createElements();
        this.parentContainer.addChild(this.container);
        window.addEventListener('keydown', this.handleKeyPress.bind(this));

        // Queries needed for debug info
        this.animalQuery = defineQuery([Animal, AI]);
        this.playerQuery = defineQuery([Player, Position]);
     }

    createElements() {
        this.background.beginFill(0x111111, 0.8); // Darker background
        this.background.drawRoundedRect(0, 0, this.config.width, this.config.height, 8);
        this.background.endFill();
        this.container.addChild(this.background);

        this.textField.position.set(this.config.padding, this.config.padding);
        this.container.addChild(this.textField);
        this.container.position.set(this.config.x, this.config.y);
    }

    handleKeyPress(e) {
        if (e.key === '?') { // Use '?' for toggle
            this.debugVisible = !this.debugVisible;
            this.container.visible = this.debugVisible;
            console.log('Debug overlay:', this.debugVisible ? 'ON' : 'OFF');
        }
    }

    update(fps, systemTimings = {}, animalTimings = {}, viewState = {}) {
        if (!this.debugVisible) {
            this.container.visible = false;
            return;
        }
        this.container.visible = true;

        let debugText = `--- DEBUG OVERLAY (?) ---\n`;
        debugText += `FPS: ${fps.toFixed(1)}\n\n`;

        // --- View State ---
        debugText += `View State:\n`;
        debugText += ` Biome: ${viewState.showBiome ? 'ON' : 'OFF'} (B)\n`;
        debugText += ` Heightmap: ${viewState.showHeightmap ? 'ON' : 'OFF'} (H)\n`;
        debugText += ` Tile Borders: ${viewState.showTileBorders ? 'ON' : 'OFF'} (L)\n`;
        debugText += ` Minimap: ${viewState.showMinimap ? 'ON' : 'OFF'} (N)\n\n`;


        // --- Player Info ---
        const players = this.playerQuery(this.world);
        if (players.length > 0) {
            const pid = players[0];
            const px = Position.x[pid]?.toFixed(1);
            const py = Position.y[pid]?.toFixed(1);
            const pChunkX = Math.floor(px / (TILE_SIZE * CHUNK_SIZE));
            const pChunkY = Math.floor(py / (TILE_SIZE * CHUNK_SIZE));
            debugText += `Player:\n`;
            debugText += ` Pos: ${px}, ${py}\n`;
            debugText += ` Chunk: ${pChunkX}, ${pChunkY}\n\n`;
        }

        // --- Entity Counts ---
         const animals = this.animalQuery(this.world);
         const plants = this.world.queries.plants(this.world);
         const rocks = this.world.queries.rocks(this.world);
         debugText += `Entities:\n`;
         debugText += ` Animals: ${animals.length}\n`;
         debugText += ` Plants: ${plants.length}\n`;
         debugText += ` Rocks: ${rocks.length}\n\n`;


        // --- Animal State Breakdown ---
        const animalStates = { IDLE: 0, MOVING: 0, EATING: 0, RESTING: 0, FLEEING: 0, UNKNOWN: 0 };
        const animalTypesCount = {};
        for (const eid of animals) {
            const stateValue = AI.state[eid];
            const stateKey = Object.keys(AI_STATE).find(key => AI_STATE[key] === stateValue) || 'UNKNOWN';
            animalStates[stateKey]++;

            if(Animal.isFleeing[eid]) animalStates['FLEEING']++; // Count fleeing separately

            const { typeName } = getEntityTypeInfo(this.world, eid);
            animalTypesCount[typeName] = (animalTypesCount[typeName] || 0) + 1;
        }
        debugText += `Animal States:\n${Object.entries(animalStates).map(([k, v]) => ` ${k}: ${v}`).join('\n')}\n\n`;
        debugText += `Animal Types:\n${Object.entries(animalTypesCount).map(([k, v]) => ` ${k}: ${v}`).join('\n')}\n\n`;

        // --- System Timings ---
        if (Object.keys(systemTimings).length > 0) {
            debugText += 'System Timings (ms):\n';
            for (const [system, time] of Object.entries(systemTimings)) {
                debugText += ` ${system}: ${typeof time === 'number' ? time.toFixed(2) : time}\n`;
            }
            debugText += '\n';
        }

        // --- Animal System Timings ---
        if (Object.keys(animalTimings).length > 0) {
             debugText += 'Animal Sys Details (ms):\n';
             for (const [op, time] of Object.entries(animalTimings)) {
                 debugText += ` ${op}: ${typeof time === 'number' ? time.toFixed(2) : time}\n`;
             }
             debugText += '\n';
        }

        // --- Add More Debug Info ---
        // e.g., Cache sizes, chunk info, selected entity details

        this.textField.text = debugText;

        // Optional: Adjust height dynamically
        const textHeight = this.textField.height;
        const newHeight = Math.max(200, textHeight + this.config.padding * 2);
        if (Math.abs(this.background.height - newHeight) > 5) {
             this.background.height = newHeight;
             this.config.height = newHeight;
        }
    }

    cleanup() {
        window.removeEventListener('keydown', this.handleKeyPress.bind(this));
        this.container.destroy({ children: true });
    }
}

class FPSCounter {
    constructor(parentContainer) {
        this.parentContainer = parentContainer;
        this.container = new Container();
        this.config = {
            width: 140,
            height: 32,
            padding: 6,
            x: 12,
            y: 12,
        };
        
        // Pixel art inspired text style
        this.textStyle = new TextStyle({
            fontFamily: '"Press Start 2P", "Courier New", monospace',
            fontSize: 12,
            fontWeight: 'normal',
            fill: 0x00FF00, // Classic green terminal color
            letterSpacing: 1,
            align: 'left'
        });
        
        this.textField = new Text('60 FPS', this.textStyle);
        this.createElements();
        this.parentContainer.addChild(this.container);
    }

    createElements() {
        // Pixel art background with retro terminal look
        const bg = new Graphics();
        
        // Main background - dark terminal
        bg.fill({ color: 0x0F0F0F });
        bg.rect(0, 0, this.config.width, this.config.height);
        
        // Pixel border - classic green
        bg.setStrokeStyle({ width: 2, color: 0x00FF00 });
        bg.rect(0, 0, this.config.width, this.config.height);
        
        // Inner border for depth
        bg.setStrokeStyle({ width: 1, color: 0x00AA00 });
        bg.rect(2, 2, this.config.width - 4, this.config.height - 4);
        
        // Scan line effect
        for (let i = 0; i < this.config.height; i += 4) {
            bg.fill({ color: 0x00FF00, alpha: 0.1 });
            bg.rect(4, i, this.config.width - 8, 1);
        }
        
        this.container.addChild(bg);

        this.textField.anchor.set(0, 0.5);
        this.textField.position.set(this.config.padding, this.config.height / 2);
        this.container.addChild(this.textField);

        this.container.position.set(this.config.x, this.config.y);
    }

    update(fps) {
        // Classic terminal color coding
        let fpsColor = 0x00FF00; // Green for good FPS
        if (fps < 30) fpsColor = 0xFF0000; // Red for bad FPS
        else if (fps < 50) fpsColor = 0xFFFF00; // Yellow for okay FPS
        
        this.textField.style.fill = fpsColor;
        this.textField.text = `${fps.toFixed(0)} FPS`;
    }

    cleanup() {
        this.container.destroy({ children: true });
    }
}


class HUDManager {
    constructor(appInstance, world) { // Pass app and world if needed by panels
        this.app = appInstance;
        this.world = world;
        this.container = new Container(); // Main HUD container added to stage
        this.container.zIndex = 1000; // Ensure HUD is on top

        // Initialize HUD components
        this.fpsCounter = new FPSCounter(this.container);
        this.entityInfoPanel = new EntityInfoPanel(this.container);
        this.minimapRenderer = new MinimapRenderer(this.container, world); // Pass world to minimap
        this.debugOverlayPanel = new DebugOverlayPanel(this.container, world); // Pass world to debug

        // Add Legends and Map Overview
        this.biomeLegend = new BiomeLegend();
        this.heightmapLegend = new HeightmapLegend();
        this.mapOverviewPanel = new MapOverviewPanel(world);
        this.container.addChild(this.biomeLegend, this.heightmapLegend, this.mapOverviewPanel);

        // Add the main HUD container to the stage
        this.app.stage.addChild(this.container);
    }

    update(worldState) { // Pass relevant state needed by HUD elements
        this.fpsCounter.update(worldState.fps);
        this.entityInfoPanel.update(this.world, worldState.selectedEntity, worldState.selectedEntityType); // Pass world reference
        this.minimapRenderer.render(worldState.playerX, worldState.playerY, worldState.showMinimap);
        this.debugOverlayPanel.update(
            worldState.fps,
            worldState.systemTimings,
            worldState.animalTimings,
            { // Pass view state object
                showBiome: worldState.showBiome,
                showHeightmap: worldState.showHeightmap,
                showTileBorders: worldState.showTileBorders,
                showMinimap: worldState.showMinimap,
                showMapOverview: worldState.showMapOverview, // Pass map overview state
            }
         );

         // Update Entity Info Panel (pass world)
         this.entityInfoPanel.update(this.world, worldState.selectedEntity, worldState.selectedEntityType); 

         // Update visibility of legends and map overview
         this.biomeLegend.visible = worldState.showBiome;
         this.heightmapLegend.visible = worldState.showHeightmap;
         this.mapOverviewPanel.update(worldState.playerX, worldState.playerY, worldState.showMapOverview);

         // Position Entity Info Panel relative to FPS counter
         this.entityInfoPanel.container.y = this.fpsCounter.container.height + 15;
    }

    // Expose methods if panels need to be triggered externally (e.g., redraw minimap)
    redrawMinimap() {
        this.minimapRenderer.setNeedsRedraw();
    }

    redrawMapOverview() {
        this.mapOverviewPanel.setNeedsRedraw();
    }

    cleanup() {
        // Clean up individual HUD components
        this.fpsCounter.cleanup();
        this.entityInfoPanel.cleanup();
        this.minimapRenderer.cleanup();
        this.debugOverlayPanel.cleanup();
        this.biomeLegend?.destroy({children: true});
        this.heightmapLegend?.destroy({children: true});
        this.mapOverviewPanel?.cleanup();

        // Remove the main HUD container from the stage and destroy it
        this.app.stage.removeChild(this.container);
        this.container.destroy({ children: true });
    }
}

// --- Main Render System Class ---
export class RenderSystem {
    constructor(appInstance, world, resources) {
        if (!appInstance || !appInstance.renderer) {
            throw new Error("[RenderSystem] Invalid PIXI app instance provided.");
        }
        console.log('[RenderSystem] Initializing...');
        app = appInstance; // Set global app instance
        setApp(app);       // Initialize subsystems needing the app (like Bee entity)

        this.app = appInstance;
        this.world = world;
        this.resources = resources;

        // --- Main World Container ---
        // This container holds tiles and entities, and its position is moved for camera effect
        this.worldContainer = new Container();
        this.worldContainer.sortableChildren = true; // Enable z-index sorting
        this.worldContainer.eventMode = 'static'; // Enable interactions for entity selection
        this.worldContainer.interactive = true;
        this.worldContainer.cursor = 'pointer'; // Default cursor
        this.app.stage.addChild(this.worldContainer);

        // --- Viewport Masking (Optional but good for performance) ---
        this.viewportMask = new Graphics();
        // Draw a rectangle slightly larger than the screen
        this.viewportMask.beginFill(0xFFFFFF);
        this.viewportMask.drawRect(-100, -100, this.app.screen.width + 200, this.app.screen.height + 200);
        this.viewportMask.endFill();
        // this.worldContainer.mask = this.viewportMask; // Apply mask to world container
        // this.app.stage.addChild(this.viewportMask); // Mask must be on stage


        // --- Component Renderers & Managers ---
        this.pool = new SpritePool();
        this.tileRenderer = new TileRenderer(this.app, this.resources);
        this.worldContainer.addChild(this.tileRenderer.container); // Add tile container to world

        this.entityRenderer = new EntityRenderer(this.world, this.worldContainer, this.resources, this.pool); // Single renderer for all entity types
        this.playerRenderer = new PlayerRenderer(this.world, this.worldContainer, this.resources); // Dedicated player renderer

        this.hud = new HUDManager(this.app, this.world); // HUD added directly to stage

        // --- Queries ---
        // Store queries directly on the instance or world if preferred
        console.log('[DEBUG] Creating queries. Structure component exists:', !!Structure);
        
        this.world.queries = {
            players: defineQuery([Player, Position, Velocity]), // Need Velocity for animation
            plants: defineQuery([Plant, Position]),
            animals: defineQuery([Animal, Position, AI]), // Need AI for debug info
            rocks: defineQuery([Rock, Position]),
            structures: defineQuery([Structure, Position]),
            // Add more queries as needed
        };
        
        console.log('[DEBUG] Queries created. Structure query exists:', !!this.world.queries.structures);
        
        // Helper function to filter entities by valid type values
        this.world.getEntitiesWithValidType = (entities, componentTypeField) => {
            return entities.filter(eid => 
                componentTypeField && 
                componentTypeField[eid] !== undefined && 
                componentTypeField[eid] > 0
            );
        };

        // --- State ---
        this.frameCount = 0;
        this.lastFPSTime = performance.now();
        this.currentFPS = 60;
        this.selectedEntity = null;
        this.selectedEntityType = null;
        this.showBiome = false;
        this.showHeightmap = false;
        this.showTileBorders = false;
        this.showMinimap = true; // Default to showing minimap
        this.showMapOverview = false; // Add state for map overview

         // --- Stats Panel (Optional) ---
        // Disabled due to initialization issues - can be re-enabled later if needed
        this.stats = null;
        console.log("[RenderSystem] Stats panel disabled");


        // --- Input Handlers ---
        this.setupInputHandlers();

        // Preload player assets
        this.playerRenderer.preload().then(() => {
             console.log("[RenderSystem] Player assets preloaded (or already ready).");
         }).catch(err => {
             console.error("[RenderSystem] Error preloading player assets:", err);
         });

         console.log('[RenderSystem] Initialization complete.');
    }

     setupInputHandlers() {
         // --- Keyboard Input ---
         window.addEventListener('keydown', this.handleKeyDown.bind(this));

         // --- Mouse Input (on world container) ---
         this.worldContainer.on('pointerdown', this.handlePointerDown.bind(this));

         // Optional: Handle deselection on background click (requires hitArea on stage/app view)
         this.app.view.interactive = true; // Ensure the canvas itself is interactive
         this.app.view.addEventListener('pointerdown', (event) => {
             // Check if the click target is the canvas/background itself, not the worldContainer or HUD
             if (event.target === this.app.view && event.button === 0) { // Left click on background
                this.deselectEntity();
             }
         });
         // Prevent default right-click menu on the canvas
         this.app.view.addEventListener('contextmenu', (e) => e.preventDefault());
     }

    handleKeyDown(e) {
        const key = e.key.toLowerCase();
        let stateChanged = false;

        if (key === 'b') {
            this.showBiome = !this.showBiome;
            if (this.showBiome) this.showHeightmap = false; // Biome overrides heightmap
            stateChanged = true;
        } else if (key === 'h') {
            this.showHeightmap = !this.showHeightmap;
            if (this.showHeightmap) this.showBiome = false; // Heightmap overrides biome
            stateChanged = true;
        } else if (key === 'l') {
            this.showTileBorders = !this.showTileBorders;
            stateChanged = true;
        } else if (key === 'n') {
            this.showMinimap = !this.showMinimap;
            stateChanged = true;
        } else if (key === 'm') { // Add listener for Map Overview
            this.showMapOverview = !this.showMapOverview;
            if(this.showMapOverview) this.hud.redrawMapOverview(); // Redraw map when shown
            stateChanged = true;
        }
        // '?' for debug overlay is handled within DebugOverlayPanel

        if (stateChanged) {
            this.hud.redrawMinimap(); // Force minimap redraw if view changes
            console.log(`[RenderSystem] View state updated: Biome=${this.showBiome}, Heightmap=${this.showHeightmap}, Borders=${this.showTileBorders}, Minimap=${this.showMinimap}`);
        }
    }

     handlePointerDown(event) {
        // Only handle right-click for selection/deselection
        if (event.data.button !== 2) return;

        const players = this.world.queries.players(this.world);
    if (players.length === 0) return;
        const playerId = players[0];
        const playerX = Position.x[playerId];
        const playerY = Position.y[playerId];

        // Get click position in world coordinates
        const worldClickPos = event.data.getLocalPosition(this.worldContainer);

        // --- Basic Lighting Check ---
        // Use current lighting radius from world state if available
        const lightingRadius = this.world.lightingSystem?.currentRadius || DAY_LIGHTING_RADIUS;
        const dxPlayer = worldClickPos.x - playerX;
        const dyPlayer = worldClickPos.y - playerY;
        if (dxPlayer * dxPlayer + dyPlayer * dyPlayer > lightingRadius * lightingRadius) {
             // Clicked outside lighting radius
             this.deselectEntity(); // Deselect if clicking in the dark
             return;
        }


        // --- Find Clicked Entity ---
        let clickedEntity = null;
        let clickedEntityType = null;
        let minDistanceSq = Infinity;

        // Log the current world state for debugging
        console.log('[SELECTION] Checking for entity under click at', worldClickPos);
        console.log('[SELECTION] Structure query length:', this.world.queries.structures(this.world).length);
        
        // Direct search for hives - check all entities with Structure component
        console.log('[SELECTION] Direct search for Structure entities:');
        const structureEntities = [];
        if (Structure && Structure.type) {
            for (let eid = 0; eid < 10000; eid++) {
                if (Structure.type[eid] !== undefined && Structure.type[eid] > 0 && 
                    Position.x[eid] !== undefined && Position.y[eid] !== undefined) {
                    structureEntities.push(eid);
                    console.log(`[SELECTION] Found Structure entity #${eid} at (${Position.x[eid]}, ${Position.y[eid]})`);
                }
            }
        }
        
        // Define entities to check (combine relevant queries)
        // Also include our directly found structure entities
        const entitiesToCheck = [
            ...this.world.queries.plants(this.world),
            ...this.world.queries.animals(this.world),
            ...this.world.queries.rocks(this.world),
            ...structureEntities, // Direct search
            ...this.world.queries.structures(this.world) // Original query
        ];

        const clickRadiusSq = (TILE_SIZE * TILE_SCALE / 2) ** 2; // Click target area (half tile size)
        const structureClickRadiusSq = (TILE_SIZE * TILE_SCALE * 0.75) ** 2; // Larger click area for structures

        for (const eid of entitiesToCheck) {
            const entityX = Position.x[eid];
            const entityY = Position.y[eid];

            // Skip entities without valid positions
            if (entityX === undefined || entityY === undefined) {
                continue;
            }

            // Check if this is a structure
            const isStructure = Structure.type && Structure.type[eid] !== undefined && Structure.type[eid] > 0;
            
            // Use larger click radius for structures
            const thisClickRadiusSq = isStructure ? structureClickRadiusSq : clickRadiusSq;

            // Simple distance check from entity center
            const dx = worldClickPos.x - entityX;
            const dy = worldClickPos.y - entityY;
            const distSq = dx * dx + dy * dy;

            if (distSq < thisClickRadiusSq && distSq < minDistanceSq) {
                 // Check if within lighting radius of player
                 const dxP = entityX - playerX;
                 const dyP = entityY - playerY;
                 if (dxP*dxP + dyP*dyP <= lightingRadius*lightingRadius) {
                    minDistanceSq = distSq;
                    clickedEntity = eid;
                    
                    // Get entity type directly using getEntityTypeInfo
                    const { entityType } = getEntityTypeInfo(this.world, eid);
                    
                    // Debug: log the entity type we're selecting
                    console.log(`[DEBUG] Selected entity #${eid} detected as type: ${entityType}`);
                    
                    // Special handling for Structure entities
                    if (Structure.type && Structure.type[eid] !== undefined) {
                        console.log(`[SELECTION] Entity #${eid} has Structure component with type=${Structure.type[eid]}`);
                        // Force the entity type to Structure if it has a valid Structure.type value > 0
                        if (Structure.type[eid] > 0) {
                            clickedEntityType = 'Structure';
                            console.log(`[SELECTION] Forcing entity type to Structure`);
                        } else {
                            console.log(`[SELECTION] Not forcing structure type because Structure.type=${Structure.type[eid]}`);
                        }
                    }
                    
                    // Make sure we only set the type if we got a valid type back
                    if (entityType !== 'Unknown' && !clickedEntityType) {
                        clickedEntityType = entityType;
                    }
                 }
            }
        }

        // Update Selection
        if (clickedEntity !== null && clickedEntity !== this.selectedEntity) {
            this.selectEntity(clickedEntity, clickedEntityType);
        } else if (clickedEntity === null) {
             this.deselectEntity(); // Clicked on empty space (or outside light)
        }
        // If clicking the already selected entity, do nothing (keep it selected)

        // Prevent default browser context menu
        event.stopPropagation(); // Stop event bubbling further
    }

    selectEntity(entityId, entityType) {
        if (this.selectedEntity === entityId) return; // No change

         // Remove highlight from previously selected (if any)
         this.deselectEntity(false); // Deselect without clearing state yet

         console.log(`[RenderSystem] Selecting Entity: ID=${entityId}, Type=${entityType}`);
         this.selectedEntity = entityId;
         this.selectedEntityType = entityType;
         
         // Special handling for Structure entities
         if (entityType === 'Structure' && Structure.type && Structure.type[entityId] > 0) {
             // Get sprite directly from world.sprites (where hives store their sprites)
             const sprite = this.world.sprites[entityId];
             if (sprite) {
                 console.log(`[RenderSystem] Directly applying outline to Structure sprite for #${entityId}`);
                 // Apply outline filter
                 sprite.filters = [new OutlineFilter(4, 0x00FF00, 0.8)]; // Green outline
                 // Boost z-index to ensure it's visible - add 500 to match entity selection system
                 sprite.zIndex += 500;
                 // Force parent container to update
                 if (sprite.parent) {
                     sprite.parent.sortChildren();
                 }
             }
         }

         // Note: Applying the filter/highlight is handled within EntityRenderer during its update loop
    }

    deselectEntity(clearState = true) {
        if (this.selectedEntity !== null) {
            console.log(`[RenderSystem] Deselecting Entity: ID=${this.selectedEntity}`);
            
            // Special handling for Structure entities - clear outline directly
            if (this.selectedEntityType === 'Structure') {
                const sprite = this.world.sprites[this.selectedEntity];
                if (sprite) {
                    console.log(`[RenderSystem] Directly removing outline from Structure sprite`);
                    sprite.filters = null; // Remove outline filter
                    // Restore z-index if it was boosted
                    if (sprite.zIndex >= 500) {
                        sprite.zIndex -= 500; 
                    }
                    // Force parent container to update
                    if (sprite.parent) {
                        sprite.parent.sortChildren();
                    }
                }
            }
            
            // Visual deselection (removing filter) happens in EntityRenderer's next update
        }
         if (clearState) {
             this.selectedEntity = null;
             this.selectedEntityType = null;
         }
    }


    setupStatsPanel() {
        // Add CSS for positioning
        const style = document.createElement('style');
        style.textContent = `
            .stats-panel {
                position: fixed;
                top: 10px;
                right: 10px;
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 8px;
                border-radius: 4px;
                font-family: monospace;
                font-size: 12px;
                z-index: 10000;
                user-select: none;
            }
        `;
        document.head.appendChild(style);
        
        // Add the stats panel to the DOM, checking if it's a valid Node
        if (this.stats.domElement instanceof Node) {
            document.body.appendChild(this.stats.domElement); 
        } else {
            console.error("[RenderSystem] Stats panel DOM element is not a valid Node or doesn't exist:", this.stats?.domElement);
        }
    }


    update() {
        if (this.stats) {
            this.stats.begin(); // Start performance measurement
        }

        // --- Get Player Info ---
        const players = this.world.queries.players(this.world);
        if (players.length === 0) {
             console.warn("[RenderSystem] No player found. Skipping render.");
             if (this.stats) this.stats.end();
             return;
        }
        const playerId = players[0];
        const playerX = Position.x[playerId];
        const playerY = Position.y[playerId];
        const playerVelX = Velocity.x[playerId];
        const playerVelY = Velocity.y[playerId];

        // --- Update FPS Counter ---
        const now = performance.now();
        this.frameCount++;
        if (now >= this.lastFPSTime + 1000) {
            this.currentFPS = (this.frameCount * 1000) / (now - this.lastFPSTime);
            this.lastFPSTime = now;
            this.frameCount = 0;
        }

        // --- Determine Visible Entities ---
        // Basic chunk-based or radius-based filtering can happen here
        // For simplicity, we'll pass most entities and let EntityRenderer cull by light
        const allPlants = this.world.queries.plants(this.world);
        const allAnimals = this.world.queries.animals(this.world);  
        const allRocks = this.world.queries.rocks(this.world);
        const allStructures = this.world.queries.structures(this.world);
        
        // EXTENSIVE DEBUGGING FOR STRUCTURES
        // console.log(`[DEBUG] All structures query result:`, allStructures);
        
        // for (const eid of allStructures) {
        //     console.log(`[DEBUG] Structure entity #${eid} found`);
        //     console.log(`[DEBUG] Structure type:`, Structure.type[eid]);
        //     console.log(`[DEBUG] Has Position:`, Position.x[eid] !== undefined);
        //     console.log(`[DEBUG] World position:`, { x: Position.x[eid], y: Position.y[eid] });
        // }
        
        // Filter to only include entities with valid type values
        const plants = this.world.getEntitiesWithValidType(allPlants, Plant.type);
        const animals = this.world.getEntitiesWithValidType(allAnimals, Animal.type);
        const rocks = this.world.getEntitiesWithValidType(allRocks, Rock.type);
        const structures = this.world.getEntitiesWithValidType(allStructures, Structure.type);
        
        // Debug: log the counts of raw queries vs. filtered entities
        // console.log(`[DEBUG] Entity counts (Raw/Filtered):`);
        // console.log(`  Plants: ${allPlants.length}/${plants.length}`);
        // console.log(`  Animals: ${allAnimals.length}/${animals.length}`);
        // console.log(`  Rocks: ${allRocks.length}/${rocks.length}`);
        // console.log(`  Structures: ${allStructures.length}/${structures.length}`);
        
        // Make sure we include the player entity
        const entitiesToRender = [...plants, ...animals, ...rocks, ...structures];
        
        // Make sure player entity is always included for EntityRenderer to track
        const playerEntities = this.world.queries.players(this.world);
        if (playerEntities.length > 0) {
            const playerId = playerEntities[0];
            if (!entitiesToRender.includes(playerId)) {
                // console.log(`[DEBUG] Adding player entity #${playerId} to entitiesToRender`);
                entitiesToRender.push(playerId);
            }
        }
        
        // --- Lighting Radius ---
        const lightingRadius = this.world.lightingSystem?.currentRadius || DAY_LIGHTING_RADIUS;


        // --- Render Components ---
        this.tileRenderer.render(this.world, playerX, playerY, this.showBiome, this.showHeightmap, this.showTileBorders);
        
        // Ensure player sprite exists before rendering entities that depend on its position
        if (this.playerRenderer.ensureSpriteExists(playerId)) {
            this.playerRenderer.render(playerX, playerY, playerVelX, playerVelY);
            
            // Force player visibility - add this debugging code temporarily
             if (this.playerRenderer.sprite) {
            //     console.log(`[DEBUG] Player sprite info:`, {
            //         visible: this.playerRenderer.sprite.visible,
            //         parent: this.playerRenderer.sprite.parent?.constructor?.name || 'none',
            //         position: `(${this.playerRenderer.sprite.x}, ${this.playerRenderer.sprite.y})`,
            //         zIndex: this.playerRenderer.sprite.zIndex,
            //         inWorldSprites: !!this.world.sprites[playerId]
            //     });
                
                // Force player to be visible
                this.playerRenderer.sprite.visible = true;
                
                // If sprite is not in any container, add it
                if (!this.playerRenderer.sprite.parent) {
                    console.log(`[DEBUG] Re-adding player sprite to container`);
                    this.worldContainer.addChild(this.playerRenderer.sprite);
                }
            }
        }

        this.entityRenderer.render(entitiesToRender, lightingRadius, playerX, playerY, this.selectedEntity);


        // --- Center Camera on Player ---
        const centerX = this.app.screen.width / 2;
        const centerY = this.app.screen.height / 2;
        this.worldContainer.x = centerX - playerX;
        this.worldContainer.y = centerY - playerY;

        // --- Update Viewport Mask Position (if used) ---
        // if (this.worldContainer.mask === this.viewportMask) {
        //     this.viewportMask.position.set(-this.worldContainer.x, -this.worldContainer.y);
        // }

         // --- Sort World Container Children (Entities) ---
         // Sorting happens based on zIndex set during entity rendering
         this.worldContainer.sortChildren();


        // --- Update HUD ---
        const hudState = {
            fps: this.currentFPS,
            playerX: playerX,
            playerY: playerY,
            selectedEntity: this.selectedEntity,
            selectedEntityType: this.selectedEntityType,
            showBiome: this.showBiome,
            showHeightmap: this.showHeightmap,
            showTileBorders: this.showTileBorders,
            showMinimap: this.showMinimap,
            showMapOverview: this.showMapOverview, // Pass map overview state
            systemTimings: this.world.systemTimings || {}, // Pass timings from world state
            animalTimings: this.world.animalTimings || {},
        };
        this.hud.update(hudState);

        if (this.stats) this.stats.end(); // End performance measurement
    }

    getRootContainer() {
        // This might be useful if external systems need access to the main world container
        return this.worldContainer;
    }

    getHUDContainer() {
        // Access to the HUD container (added directly to stage)
        return this.hud.container;
    }
    
    getQueries() {
        // Return all queries for use by other systems
        return this.world.queries;
    }

    cleanup() {
        console.log('[RenderSystem] Cleaning up...');
        // Remove event listeners
        window.removeEventListener('keydown', this.handleKeyDown.bind(this));
        // Clean up mouse listeners if added directly to app.view or worldContainer
        if(this.worldContainer) {
            this.worldContainer.off('pointerdown', this.handlePointerDown.bind(this));
            this.worldContainer.destroy({ children: true }); // Destroy world container and its children
        }
        if (this.app.view.interactive) {
             // Remove listeners added to app.view if any
             // e.g., this.app.view.removeEventListener('pointerdown', ...);
             this.app.view.removeEventListener('contextmenu', (e) => e.preventDefault());
        }


        // Cleanup components
        this.tileRenderer?.cleanup();
        this.entityRenderer?.cleanupAll();
        this.playerRenderer?.cleanup();
        this.hud?.cleanup(); // HUD cleanup removes its container from stage
        this.pool?.clear(); // Clear sprite pool

        // Cleanup Stats panel
        if (this.stats && this.stats.domElement && this.stats.domElement.parentElement) {
            document.body.removeChild(this.stats.domElement);
        }
        // Remove stats CSS if added
        const statsStyle = document.head.querySelector('style[data-stats-style]'); // Add an attribute to find it
        if (statsStyle) document.head.removeChild(statsStyle);


        // Clear caches
        textureCache.clear();
        playerFrames.clear();
        playerSpritesheet = null;
        playerSpritesheetReady = false;
        app = null; // Clear global app reference

        console.log('[RenderSystem] Cleanup complete.');
    }
}

// Export the main system and potentially initialization function
export function initRenderSystem(app, world, resources) {
    console.log('[RENDER] Initializing render system');
    console.log('[RENDER] App reference:', app ? 'valid' : 'null/undefined');
    console.log('[RENDER] App renderer:', app && app.renderer ? 'available' : 'missing');
    
    // Create world container if it doesn't exist
    if (!app.worldContainer) {
        console.log('[RENDER] Creating world container');
        app.worldContainer = new Container();
        app.worldContainer.name = 'worldContainer';
        app.stage.addChild(app.worldContainer);
        console.log('[RENDER] World container created and added to stage');
    } else {
        console.log('[RENDER] World container already exists');
    }
    
    // Store app reference
    window.app = app;
    
    // Create render system instance
    const renderSystem = new RenderSystem(app, world, resources);
    console.log('[RENDER] Render system initialized');
    
    return renderSystem;
}

// --- Add Legend Classes ---
class LegendPanel extends Container {
    constructor(title, items, config = {}) {
        super();
        this.config = {
            width: 180,
            padding: 10,
            itemHeight: 18,
            titleSize: 12,
            itemSize: 11,
            bgColor: 0x000000,
            bgAlpha: 0.7,
            textColor: 0xFFFFFF,
            itemSpacing: 4,
            x: window.innerWidth - (config.width || 180) - 10,
            y: config.y || 10, // Default Y, can be overridden
            ...config, // Allow overriding defaults
        };
        this.items = items;
        this.panelTitle = title;

        this.background = new Graphics();
        this.titleText = new Text(this.panelTitle, { 
            fontFamily: 'Arial', fontSize: this.config.titleSize, fill: this.config.textColor 
        });
        this.itemContainer = new Container();

        this.createElements();
        this.position.set(this.config.x, this.config.y);
        this.visible = false; // Hidden by default
    }

    createElements() {
        // Title
        this.titleText.anchor.set(0.5, 0);
        this.titleText.x = this.config.width / 2;
        this.titleText.y = this.config.padding;
        this.addChild(this.titleText);

        // Items
        this.itemContainer.position.set(this.config.padding, this.config.padding * 1.5 + this.config.itemSpacing * 2);
        this.addChild(this.itemContainer);

        let currentY = 0;
        const itemStyle = { fontFamily: 'Arial', fontSize: this.config.itemSize, fill: this.config.textColor };

        this.items.forEach(item => {
            const itemRow = new Container();
            itemRow.y = currentY;

            // Color Box/Indicator
            if (item.color !== undefined) {
                const box = new Graphics();
                box.beginFill(item.color);
                box.drawRect(0, 0, this.config.itemSize, this.config.itemSize);
                box.endFill();
                itemRow.addChild(box);
            }
            // TODO: Add support for gradient bar for heightmap

            // Text Label
            const text = new Text(item.name, itemStyle);
            text.position.set(this.config.itemSize + this.config.itemSpacing, 0);
            itemRow.addChild(text);

            this.itemContainer.addChild(itemRow);
            currentY += this.config.itemHeight;
        });

        // Background (drawn last to be behind text)
        const totalHeight = this.itemContainer.y + currentY + this.config.padding;
        this.background.beginFill(this.config.bgColor, this.config.bgAlpha);
        this.background.drawRoundedRect(0, 0, this.config.width, totalHeight, 8);
        this.background.endFill();
        this.addChildAt(this.background, 0); // Add background behind everything
        
        // Recalculate X position based on final width
        this.config.x = window.innerWidth - this.config.width - 10;
        this.x = this.config.x;
    }
    
    updatePosition() {
         this.x = window.innerWidth - this.width - 10;
    }
}

class BiomeLegend extends LegendPanel {
    constructor() {
        const biomeItems = Object.entries(BIOME_COLORS).map(([name, color], index) => ({
            name: name.charAt(0) + name.slice(1).toLowerCase(), // Capitalize
            color: color,
            // Optional: use BIOME_TYPES enum if order matters
            // name: Object.keys(BIOME_TYPES).find(key => BIOME_TYPES[key] === index) || name 
        }));
        super('Biome Legend', biomeItems, { y: 150 }); // Position below other legends potentially
    }
}

class HeightmapLegend extends LegendPanel {
     constructor() {
         // Simplified gradient representation with text
         const heightItems = [
             { name: 'Low', color: getHeightColor(0.1) }, 
             { name: ' ', color: getHeightColor(0.3) }, 
             { name: ' ', color: getHeightColor(0.5) }, 
             { name: ' ', color: getHeightColor(0.7) }, 
             { name: 'High', color: getHeightColor(0.9) },
         ];
         // TODO: Implement a proper gradient bar graphic if desired
         super('Heightmap Legend', heightItems, { y: 10, width: 100 }); // Smaller width, position top
     }
}

// Add Map Overview Panel
class MapOverviewPanel extends Container {
     constructor(world) {
        super();
        this.world = world;
        this.config = {
            maxWidth: 250,
            maxHeight: 250,
            padding: 15,
            x: window.innerWidth - (this.config?.maxWidth || 250) - 10, // Initial estimate
            y: 10, 
            bgColor: 0x000000,
            bgAlpha: 0.7,
            playerDotColor: 0xFF0000,
            playerDotSize: 3,
            titleSize: 12,
            textColor: 0xFFFFFF,
        };
        
        this.mapSprite = new Sprite(Texture.WHITE); // Placeholder
        this.playerDot = new Graphics();
        this.background = new Graphics();
        this.titleText = new Text('Map Overview (M)', { fontFamily: 'Arial', fontSize: this.config.titleSize, fill: this.config.textColor });
        this.mapTexture = null;
        this.needsRedraw = true;
        
        this.calculateDimensions();
        this.createElements();
        this.updatePosition();
        this.visible = false; // Hidden by default
        window.addEventListener('resize', this.updatePosition.bind(this));
     }
     
     calculateDimensions() {
        const mapAspectRatio = MAP_WIDTH / MAP_HEIGHT;
        if (mapAspectRatio > 1) {
            this.panelWidth = this.config.maxWidth;
            this.panelHeight = this.config.maxWidth / mapAspectRatio;
            } else {
            this.panelHeight = this.config.maxHeight;
            this.panelWidth = this.config.maxHeight * mapAspectRatio;
        }
        this.totalWidth = this.panelWidth + this.config.padding * 2;
        this.totalHeight = this.panelHeight + this.config.padding * 3 + this.config.titleSize; // Extra padding for title
     }

     createElements() {
        // Title
        this.titleText.anchor.set(0.5, 0);
        this.titleText.x = this.totalWidth / 2;
        this.titleText.y = this.config.padding / 2;
        this.addChild(this.titleText);

        // Map Sprite Container (for positioning and scaling map)
        const mapContainer = new Container();
        mapContainer.position.set(this.config.padding, this.config.padding * 1.5 + this.config.titleSize);
        mapContainer.addChild(this.mapSprite);
        this.addChild(mapContainer);

        // Player Dot
        this.playerDot.beginFill(this.config.playerDotColor);
        this.playerDot.drawCircle(0, 0, this.config.playerDotSize);
        this.playerDot.endFill();
        mapContainer.addChild(this.playerDot); // Add dot relative to map container

        // Background
        this.background.beginFill(this.config.bgColor, this.config.bgAlpha);
        this.background.drawRoundedRect(0, 0, this.totalWidth, this.totalHeight, 8);
        this.background.endFill();
        this.addChildAt(this.background, 0);
     }
     
     updatePosition() {
         this.x = window.innerWidth - this.totalWidth - 10;
         this.y = this.config.y;
     }

     setNeedsRedraw() {
        this.needsRedraw = true;
     }

     update(playerX, playerY, showMap) {
        this.visible = showMap;
        if (!showMap) return;

        if (this.needsRedraw) {
            this.redrawMapTexture();
            this.needsRedraw = false;
        }

        // Update Player Dot Position
        const scaleX = this.panelWidth / MAP_WIDTH;
        const scaleY = this.panelHeight / MAP_HEIGHT;
        const mapPlayerX = (playerX / TILE_SIZE) * scaleX;
        const mapPlayerY = (playerY / TILE_SIZE) * scaleY;
        this.playerDot.position.set(mapPlayerX, mapPlayerY);
     }
     
     redrawMapTexture() {
         if (!app || !app.renderer) return;
         
         const mapGraphics = new Graphics();
         const scale = 1; // Draw 1 pixel per tile for texture
         
         const tileColors = new Map();
         for (let x = 0; x < MAP_WIDTH; x++) {
             for (let y = 0; y < MAP_HEIGHT; y++) {
                 const tile = tileManager.getTile(x, y);
                 if (tile) {
                     let color = 0x8B4513; // Default
                     if (tile.type === TILE_TYPES.WATER) {
                         color = BIOME_COLORS.WATER || 0x1E90FF;
                     } else {
                         const biomeKey = Object.keys(BIOME_COLORS)[tile.biome];
                         color = BIOME_COLORS[biomeKey] || 0x8B4513;
                     }
                     if (!tileColors.has(color)) tileColors.set(color, []);
                     tileColors.get(color).push({ x: x * scale, y: y * scale, w: scale, h: scale });
                 }
             }
         }
         for (const [color, rects] of tileColors) {
             mapGraphics.beginFill(color);
             rects.forEach(r => mapGraphics.drawRect(r.x, r.y, r.w, r.h));
             mapGraphics.endFill();
         }

        // Create/update render texture
        if (this.mapTexture) this.mapTexture.destroy(true);
        this.mapTexture = PIXI.RenderTexture.create({ width: MAP_WIDTH, height: MAP_HEIGHT, scaleMode: SCALE_MODES.NEAREST });
        app.renderer.render(mapGraphics, { renderTexture: this.mapTexture });
        mapGraphics.destroy();

        this.mapSprite.texture = this.mapTexture;
        this.mapSprite.width = this.panelWidth;
        this.mapSprite.height = this.panelHeight;
     }

     cleanup() {
         window.removeEventListener('resize', this.updatePosition.bind(this));
         if (this.mapTexture) this.mapTexture.destroy(true);
         this.destroy({ children: true });
     }
}

// Debug function to force spawn a hive and bees near the player
let debugHiveSpawned = false;

export function debugForceSpawnHiveAndBees(world, playerX, playerY) {
    // Skip if we've already spawned a debug hive
    if (debugHiveSpawned) return;
    
    console.log('[DEBUG] Force-spawning a hive and bees near the player');
    
    // Spawn a hive a bit to the right and above the player
    const hiveX = playerX + TILE_SIZE * 3;
    const hiveY = playerY - TILE_SIZE * 2;
    
    // Create the hive
    const hive = createHive(world, hiveX, hiveY);
    if (!hive) {
        console.error('[DEBUG] Failed to spawn debug hive!');
        return;
    }
    
    console.log(`[DEBUG] Successfully spawned debug hive #${hive} at (${hiveX}, ${hiveY})`);
    
    // Spawn bees around the hive
    const bees = [];
    for (let i = 0; i < 5; i++) {
        const angle = (i / 5) * Math.PI * 2;
        const distance = 32;
        const beeX = hiveX + Math.cos(angle) * distance;
        const beeY = hiveY + Math.sin(angle) * distance;
        
        // Pass Position component explicitly
        const bee = createBee(world, beeX, beeY, hive, Position);
        if (bee) {
            bees.push(bee);
            console.log(`[DEBUG] Spawned bee #${bee} at (${beeX.toFixed(1)}, ${beeY.toFixed(1)})`);
        }
    }
    
    console.log(`[DEBUG] Spawned ${bees.length} bees for debug hive`);
    debugHiveSpawned = true;
}
